import React, { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '../supabaseClient';
import { changesSeenAt } from '../lib/changesSeen';
import { useNavigate, useLocation } from 'react-router-dom';
import FusionLogo from '../components/FusionLogo';
import SupplierFooter from '../components/SupplierFooter';
import Badge from '../components/Badge';
import { portalStatusMeta } from '../components/portal/PortalStatusPill';
import AwardMedal from '../components/AwardMedal';
import StatusHexagon from '../components/portal/StatusHexagon';
import { ConfettiBurst } from '../components/confetti';
import Modal from '../components/Modal';
import SupplierAccountMenu from '../components/SupplierAccountMenu';
import SupplierSlideout from '../components/SupplierSlideout';
import ComplianceBadge from '../components/compliance/ComplianceBadge';
import ComplianceBar from '../components/compliance/ComplianceBar';
import InfoIcon from '../components/InfoIcon';
import { isEcoStage, ecoStageToFrame } from '../lib/ecoAward';
import OakStatusBadge from '../components/compliance/OakStatusBadge';
import { deriveClosedOutcome, CLOSED_OUTCOME_COPY } from '../lib/closedBriefOutcome';
import Alert from '../components/Alert';

import SupplierTour from '../components/SupplierTour';
import { infoTipsEnabled, setInfoTipsEnabled } from '../lib/infoTips';
import { useTour, openTour, closeTour, recordTourSeen, getTourState, SUPPLIER_TOUR_STEPS } from '../lib/supplierTour';
import { deadlineUrgency, formatDeadlineLondon } from '../lib/deadlineUrgency';
import { useNow } from '../hooks/useNow';
import { useSupplierCompliance } from '../hooks/useSupplierCompliance';
import { approvedCurrentCount } from '../lib/compliance';
import { extractName } from '../lib/contactName';
import '../lib/award-pulse.css';
import ArchiveIcon from '../components/ArchiveIcon';
import { fmtDate } from '../lib/dates';
import { useActionToast } from '../components/ActionToast';
import '../components/archiveAnimation.css';

// Soft category chips (BRIEF-32 follow-up) – each activity/recognition
// category gets its own gentle tint + matching icon colour so the "What's
// new" and recognition strips read as distinct signals rather than one
// repeated green. Growth/reward categories are greens (each a slightly
// different shade); the trust categories – compliance + partnership – are a
// cooler blue-slate. Pale bg + colour-matched icon, per the mock.
const CHIP_SHADE = {
  award:       { bg: '#EAF2DC', fg: '#4B951F' },
  compliance:  { bg: '#E3EAF0', fg: '#5A7789' },
  eco:         { bg: '#E6F1D6', fg: '#3C8A2C' },
  partnership: { bg: '#E3EAF0', fg: '#5A7789' },
  safety:      { bg: '#E1EFE4', fg: '#2C8A5B' },
};
const CHIP_FALLBACK = { bg: '#E9F0DE', fg: '#5B7A3A' };

// Supplier portal dashboard. Briefs-first by design – the supplier's
// primary job here is responding to briefs, so that section dominates
// the body. Compliance lives behind the sticky stripe at the top and
// on its own page at /portal/compliance; the document library moved
// there too. Recognition (badges) sits at the foot as a quieter
// engagement surface.
//
// State-adaptive: when the supplier is Red (any required doc Outstanding,
// Expired, or still in Awaiting Review), the Awaiting your response tab's
// empty state explains that briefs can still be responded to, but Fusion 2K
// can't award work until compliance is resolved. In Progress / Awarded /
// Closed tabs stay fully functional regardless of state – briefs in flight
// continue normally; only new awards are gated (handled on the Alchemy
// side, not here).
// "Changed" glyph (refresh) for the merged changed-brief banner – carries the
// meaning in the icon so it doesn't rest on amber colour alone (Alert §1/§3).
const CHANGED_GLYPH = (
  <>
    <polyline points="23 4 23 10 17 10" />
    <polyline points="1 20 1 14 7 14" />
    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
  </>
);

export default function SupplierPortal() {
  const navigate = useNavigate();
  const [briefs, setBriefs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [supplierUser, setSupplierUser] = useState(null);
  // Closed-brief outcome surfacing (Brief 3 §5): the supplier's own responses
  // keyed by brief_id, to tell draft_unsent from no_response in What's New.
  const [responsesByBrief, setResponsesByBrief] = useState(new Map());
  // §5/§7 – per-brief count of clarifications PUBLISHED after this supplier
  // submitted, so a submitted supplier is warned their quote may be stale.
  const [newClarByBrief, setNewClarByBrief] = useState(new Map());
  // Onboarding tour: the shared store (lib/supplierTour) drives the spotlight
  // overlay so its step survives the hop onto the compliance page and back.
  // tourAutoChecked guards the once-per-load first-login auto-run. Replay
  // lives in the account menu (handleReplayTour).
  const tour = useTour();
  // Live clock for deadline countdowns – recomputes urgency over time (§5).
  const nowMs = useNow();
  const [tourAutoChecked, setTourAutoChecked] = useState(false);
  const [supplierBadges, setSupplierBadges] = useState([]);
  const [badgeCategoryMap, setBadgeCategoryMap] = useState(new Map());
  const [earnModalBadge, setEarnModalBadge] = useState(null);
  const [slideoutOpen, setSlideoutOpen] = useState(false);
  const [slideoutTab, setSlideoutTab] = useState('profile');
  const openSlideout = (tab) => { setSlideoutTab(tab); setSlideoutOpen(true); };
  // BRIEF-25 §2 – briefs are the wide hero with an "All"-default filter.
  // Smart landing: default to Awaiting you; once briefs load, if Awaiting is
  // empty but there are In-progress briefs, land there instead; if both are
  // empty, stick to Awaiting. Applied once (didInitTab) so it never overrides
  // a manual tab click.
  const [activeBriefTab, setActiveBriefTab] = useState('awaiting');
  const didInitTab = useRef(false);
  // Per-supplier archive: the set of brief_ids this supplier has archived
  // from their own view (one-way), and the Closed-tab "show archived" toggle.
  const [supplierArchivedSet, setSupplierArchivedSet] = useState(() => new Set());
  // Two-phase animation gates for the archive choreography. archiving =
  // slide+fade (phase 1). collapsing = max-height/padding/border collapse
  // (phase 2, starts 150ms later via setTimeout). Once both phases are
  // done we add the briefId to supplierArchivedSet which is the
  // truth-state read by isArchivedForSupplier (filters the row out).
  const [supplierArchivingIds, setSupplierArchivingIds] = useState(() => new Set());
  const [supplierCollapsingIds, setSupplierCollapsingIds] = useState(() => new Set());
  // Toast for supplier-side archive confirmation. One-way archive per
  // spec – no Undo button on this side; the toast is feedback only.
  const { notify: supplierNotify, Toast: SupplierToast } = useActionToast();
  const [showArchivedBriefs, setShowArchivedBriefs] = useState(false);
  // Independent toggle for the Awarded tab – an archived AWARDED brief
  // belongs to the supplier's wins history, not the lost-briefs pile,
  // so it gets its own "Show archived" affordance rather than being
  // dumped together with archived-closed under the Closed tab.
  const [showArchivedAwarded, setShowArchivedAwarded] = useState(false);
  // Award acknowledgements: brief_id → acknowledgement row for this supplier.
  // Rows with acknowledged_at IS NULL – or absent entirely – are "unacknowledged
  // awards" and drive the four celebration cues (welcome panel, callout strip,
  // fresh card, tab pulse). The row is written on first open of the awarded
  // brief's detail page; until then the cues stay active.
  const [awardAcks, setAwardAcks] = useState(() => new Map());
  // Primary brief-owner lookup keyed by brief_id. Populated from the
  // recipients query embed (briefs.owner) so the fresh-award card and
  // any future supplier-facing surface can name the PM without an extra
  // round-trip. Only ever holds the brief's primary owner – secondary
  // brief_owners list is intentionally NOT used here (spec point 6).
  // Owner map is still populated on load for potential downstream use; the
  // compact brief rows no longer read it, so only the setter is bound.
  const [, setBriefOwners] = useState(() => new Map());
  // Distinguishes "couldn't load" from "you have no briefs". A failed
  // briefs query (e.g. a missing-column error after a deploy that lands
  // before its migration) was previously swallowed into an empty list,
  // making it look like the supplier had been silently un-recipiented
  // from everything. Setting this flag lets the empty-list path render
  // a recoverable error message instead of a confidently-wrong "no
  // briefs sent to you yet" copy.
  const [briefsLoadError, setBriefsLoadError] = useState(null);

  useEffect(() => {
    (async () => {
      const { data: { user } } = await supabase.auth.getUser();
      const { data: su } = await supabase
        .from('supplier_users')
        .select('*, suppliers(*)')
        .eq('id', user.id)
        .single();
      setSupplierUser(su);
      if (su) {
        const { data: recipients, error: recipientsErr } = await supabase
          .from('brief_recipients')
          .select('*, briefs!inner(id, brief_name, fusion_project_name, brief_category, brief_type, required_response_time, brief_content, nda_required, status, awarded_supplier_id, awarded_amount, closed_at, archived_at, client, event_live_date, event_live_end_date, show_client, show_event_date, not_selected_body, not_selected_overrides, owner:internal_users!owner_id(id, full_name, email, is_active))')
          .eq('supplier_id', su.supplier_id)
          .in('briefs.status', ['sent', 'closed']);
        if (recipientsErr) {
          // Surface to the console so deploy-time issues are visible to
          // staff inspecting a supplier's session; UI gets a clear
          // recoverable banner via briefsLoadError below.
          console.error('SupplierPortal: failed to load briefs', recipientsErr);
          setBriefsLoadError(recipientsErr.message || 'Could not load your briefs.');
        }
        setBriefs(recipients || []);
        // Primary-owner map keyed by brief_id – spec point 6: primary only,
        // brief_owners (secondary list) is intentionally NOT used here.
        // Inactive owners are skipped so the no-PM fallback copy kicks in.
        const ownerMap = new Map();
        for (const r of recipients || []) {
          const o = r.briefs?.owner;
          if (!o || o.is_active === false) continue;
          const name = extractName(o.full_name);
          if (!name) continue;
          ownerMap.set(r.briefs.id, {
            name,
            firstName: name.split(' ')[0],
            email: o.email || null,
          });
        }
        setBriefOwners(ownerMap);
        const { data: archs } = await supabase
          .from('supplier_brief_archives')
          .select('brief_id')
          .eq('supplier_id', su.supplier_id);
        setSupplierArchivedSet(new Set((archs || []).map(a => a.brief_id)));
        // The supplier's own responses (to distinguish a started-but-unsent
        // draft from no response at all) + this user's dismissed outcome
        // notices (Brief 3 §5). Both best-effort – surfacing degrades quietly.
        const { data: myResponses } = await supabase
          .from('brief_responses')
          .select('brief_id, is_submitted, last_autosaved_at, submitted_at, total_price')
          .eq('supplier_id', su.supplier_id);
        setResponsesByBrief(new Map((myResponses || []).map(r => [r.brief_id, r])));
        // §5/§7 – for each brief this supplier has SUBMITTED, count clarifications
        // published after their submission. Read from the anonymised view (no
        // asker identity), scoped to their own briefs.
        const submittedByBrief = new Map((myResponses || [])
          .filter(r => r.is_submitted && r.submitted_at)
          .map(r => [r.brief_id, r.submitted_at]));
        const submittedBriefIds = Array.from(submittedByBrief.keys());
        if (submittedBriefIds.length) {
          const { data: pub } = await supabase
            .from('brief_published_questions')
            .select('brief_id, published_at')
            .in('brief_id', submittedBriefIds);
          // { count, latestPublishedAt } per brief. The timestamp is what lets
          // the changed-brief banner tell "clarification the supplier hasn't
          // seen" from "clarification they already read" – a bare count can
          // only ever say something exists, never whether it's new to them.
          const counts = new Map();
          (pub || []).forEach(p => {
            const subAt = submittedByBrief.get(p.brief_id);
            if (subAt && p.published_at && new Date(p.published_at).getTime() > new Date(subAt).getTime()) {
              const prev = counts.get(p.brief_id) || { count: 0, latestPublishedAt: null };
              counts.set(p.brief_id, {
                count: prev.count + 1,
                latestPublishedAt: (!prev.latestPublishedAt
                  || new Date(p.published_at).getTime() > new Date(prev.latestPublishedAt).getTime())
                  ? p.published_at : prev.latestPublishedAt,
              });
            }
          });
          setNewClarByBrief(counts);
        }
        // Award acknowledgement rows for this supplier – keyed by brief
        // so per-brief lookup is constant-time. We don't filter by
        // acknowledged_at server-side: the celebration cues need to know
        // about acknowledged awards too (so a card can flip to the calm
        // state once the supplier has opened it).
        const { data: ackRows } = await supabase
          .from('supplier_award_acknowledgements')
          .select('brief_id, awarded_at, acknowledged_at')
          .eq('supplier_id', su.supplier_id);
        setAwardAcks(new Map((ackRows || []).map(a => [a.brief_id, a])));
        const [badgesRes, catsRes] = await Promise.all([
          supabase
            .from('supplier_badges')
            .select('id, category_id, tier, awarded_at, expires_at, first_seen_at, action_needed')
            .eq('supplier_id', su.supplier_id),
          supabase
            .from('badge_categories')
            .select('id, slug, name, bronze_name, silver_name, gold_name'),
        ]);
        setSupplierBadges(badgesRes.data || []);
        setBadgeCategoryMap(new Map((catsRes.data || []).map(c => [c.id, c])));
      }
      setLoading(false);
    })();
  }, []);

  // Surface a freshly-earned badge (first_seen_at = null) via the Earn
  // modal once the supplier has been loaded. Only fires when a candidate
  // exists; firing the modal stays as a one-shot per badge – dismissing
  // writes first_seen_at so the modal doesn't re-show on next visit.
  useEffect(() => {
    if (earnModalBadge) return;
    const unseen = supplierBadges.filter(b => !b.first_seen_at);
    if (unseen.length === 0) return;
    // Eco only celebrates at Oak Status (full oak). An unseen eco badge
    // below Oak Status – a legacy metal-tier row, or a row whose stage was
    // set before the migration landed – must NOT fire the modal (it would
    // show an acorn under an "Oak Status" headline). Silently stamp those
    // so they stop qualifying, then let a genuine Oak Status / non-eco
    // badge celebrate on the next pass.
    const ecoBelowOak = unseen.filter(b => {
      const cat = badgeCategoryMap.get(b.category_id);
      return cat?.slug === 'eco' && b.tier !== 'oak_status';
    });
    if (ecoBelowOak.length > 0) {
      const nowIso = new Date().toISOString();
      const ids = ecoBelowOak.map(b => b.id);
      const idSet = new Set(ids);
      setSupplierBadges(prev => prev.map(x => idSet.has(x.id) ? { ...x, first_seen_at: nowIso } : x));
      supabase.from('supplier_badges').update({ first_seen_at: nowIso }).in('id', ids).then(() => {});
      return;
    }
    setEarnModalBadge(unseen[0]);
  }, [supplierBadges, earnModalBadge, badgeCategoryMap]);

  const dismissEarnModal = async () => {
    const b = earnModalBadge;
    setEarnModalBadge(null);
    if (!b) return;
    const now = new Date().toISOString();
    setSupplierBadges(prev => prev.map(x => x.id === b.id ? { ...x, first_seen_at: now } : x));
    await supabase.from('supplier_badges').update({ first_seen_at: now }).eq('id', b.id);
  };

  // Award celebration modal (award-modal brief §2). Fires ONCE per award, on
  // the first portal view after it's made – tracked per-device in localStorage
  // so it never repeats on later logins. Independent of the persistent banner
  // (which stays until the award is acknowledged – dismissing the modal costs
  // nothing). SUPPRESSED when a more urgent deadline needs attention (a brief
  // closing today / overdue) so it never sits between the supplier and a
  // closing deadline; it simply waits for the next session. Multiple awards
  // collapse into ONE modal with a list, mirroring the banner.
  const AWARD_MODAL_SEEN_KEY = 'sp_award_modal_seen';
  const [awardModalAwards, setAwardModalAwards] = useState(null);
  const awardModalResolvedRef = useRef(false);
  useEffect(() => {
    if (awardModalResolvedRef.current) return;
    if (loading || briefs.length === 0) return; // wait for the first real load
    awardModalResolvedRef.current = true;
    let seen = [];
    try { seen = JSON.parse(localStorage.getItem(AWARD_MODAL_SEEN_KEY) || '[]'); } catch { seen = []; }
    const fresh = briefs.filter(r => isAwardedToMe(r) && !isArchivedForSupplier(r) && !seen.includes(r.brief_id));
    if (fresh.length === 0) return;
    const urgentDeadline = briefs.some(r => {
      if (r.briefs?.status === 'closed' || r.status === 'responded' || r.status === 'declined') return false;
      const u = deadlineUrgency(r.response_deadline || r.briefs?.required_response_time, r);
      return u && (u.band === 'imminent' || u.band === 'overdue');
    });
    if (urgentDeadline) return; // don't block a closing deadline – next session
    setAwardModalAwards(fresh);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [briefs, loading]);

  const dismissAwardModal = () => {
    const ids = (awardModalAwards || []).map(r => r.brief_id);
    try {
      const seen = JSON.parse(localStorage.getItem(AWARD_MODAL_SEEN_KEY) || '[]');
      localStorage.setItem(AWARD_MODAL_SEEN_KEY, JSON.stringify([...new Set([...seen, ...ids])]));
    } catch { /* localStorage unavailable – modal simply re-checks next load */ }
    setAwardModalAwards(null);
  };

  // Compliance state for the sticky stripe + briefs state-adaptation.
  // Shared with /portal/compliance via the same hook so the stripe says
  // the same thing on both pages.
  const compliance = useSupplierCompliance({
    supplierId: supplierUser?.supplier_id,
    riskFlag: supplierUser?.suppliers?.risk_flag || null,
  });

  // First-login auto-run: tips on AND tour not yet seen → spotlight tour.
  // Fires once per load (tourAutoChecked) so re-renders don't re-open it.
  useEffect(() => {
    if (loading || !supplierUser || tourAutoChecked) return;
    setTourAutoChecked(true);
    const s = supplierUser.suppliers;
    // Require the flag to be a real boolean – pre-migration the column is
    // absent, and we don't want to auto-run the tour every login until the
    // onboarding migration has landed.
    // Don't re-open a tour that's already in progress. The final step
    // ('done') navigates /portal/compliance -> /portal, which REMOUNTS this
    // page and re-runs this effect; without this guard it would call openTour()
    // (tour_seen isn't stamped until finish) and reset the walkthrough back to
    // step 1, so the supplier could never reach/complete the last step.
    if (s && typeof s.onboarding_tips_enabled === 'boolean'
        && s.onboarding_tips_enabled && !s.onboarding_tour_seen
        && !getTourState().open) {
      openTour();
    }
  }, [loading, supplierUser, tourAutoChecked]);

  // Persist on finish OR skip: mark seen + stamp the completion time.
  // Optimistic local update so the state reflects without a refetch. Tips
  // on/off is admin-controlled (it gates the first-login auto-run); the
  // supplier replays from the account menu rather than a self-toggle.
  const handleTourClose = useCallback(async () => {
    closeTour();
    const supplierId = supplierUser?.supplier_id;
    if (!supplierId) return;
    const nowIso = new Date().toISOString();
    setSupplierUser(prev => prev ? { ...prev, suppliers: { ...prev.suppliers, onboarding_tour_seen: true, onboarding_tour_completed_at: nowIso } } : prev);
    await recordTourSeen(supplierId);
  }, [supplierUser]);

  // Final-step "Show these tips when I sign in" toggle → onboarding_tips_enabled
  // (off = no auto-run and no quiet launcher on future logins). Persists live.
  const handleTipsToggle = useCallback(async (enabled) => {
    const supplierId = supplierUser?.supplier_id;
    if (!supplierId) return;
    setSupplierUser(prev => prev ? { ...prev, suppliers: { ...prev.suppliers, onboarding_tips_enabled: enabled } } : prev);
    await supabase.from('suppliers').update({ onboarding_tips_enabled: enabled }).eq('id', supplierId);
  }, [supplierUser]);
  const tipsEnabled = supplierUser?.suppliers?.onboarding_tips_enabled !== false;

  // Replay the tour from the account menu (top-right). Available on every
  // login; doesn't change any flags.
  const handleReplayTour = () => openTour();

  // Replay triggered from another supplier page's account menu (it navigates
  // here with location state, since the tour starts on the portal home).
  // Fire once per arrival.
  const location = useLocation();
  const replayHandledRef = useRef(false);
  useEffect(() => {
    if (location.state?.replayTour && !replayHandledRef.current) {
      replayHandledRef.current = true;
      openTour();
    }
  }, [location.state]);

  // Smart landing tab – once briefs first arrive, prefer Awaiting you, else In
  // progress, else stay on Awaiting. Runs once; never overrides a manual click.
  useEffect(() => {
    if (didInitTab.current || !briefs || briefs.length === 0) return;
    didInitTab.current = true;
    const isArch = (r) => Boolean(r.briefs?.archived_at) || supplierArchivedSet.has(r.brief_id);
    const live = briefs.filter(r => r.status !== 'withdrawn' && !isArch(r) && r.briefs?.status !== 'closed');
    const aw = live.filter(r => r.status === 'pending').length;
    const ip = live.filter(r => r.status === 'accepted').length;
    if (aw === 0 && ip > 0) setActiveBriefTab('in_progress');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [briefs]);

  if (loading) return (
    <div className="min-h-screen bg-canvas flex items-center justify-center">
      <p className="font-['Metropolis'] text-[#51534A] tracking-[3px] uppercase text-sm">Loading...</p>
      {/* Hold the tour's dim continuous if it's returning to the portal
          mid-walkthrough – ready=false waits until the anchors exist. */}
      {tour.open && (
        <SupplierTour
          steps={SUPPLIER_TOUR_STEPS}
          currentPath={location.pathname}
          ready={false}
          onNavigate={navigate}
          onClose={handleTourClose}
          tipsEnabled={tipsEnabled}
          onTipsToggle={handleTipsToggle}
          infoTipsOn={infoTipsEnabled()}
          onInfoTipsToggle={setInfoTipsEnabled}
        />
      )}
    </div>
  );

  // Single bucket per recipient, factoring in BOTH the brief lifecycle
  // status AND the supplier's response status.
  //   in_progress = supplier has accepted the brief but not yet
  //                 submitted a response
  //   submitted   = supplier has submitted a response and is waiting
  //                 for Fusion to award / close the brief
  //
  // 'withdrawn' returns null so the brief vanishes from every tab –
  // this is the supplier whose award was just taken back. Fusion is
  // handling that conversation off-platform; surfacing the brief
  // anywhere on this portal would be misleading.
  // Archived for THIS supplier = Fusion archived it globally OR the supplier
  // archived it from their own view. Either way it leaves their normal tabs.
  // Auto-archive (Brief 3 §6): a closed brief older than 60 days moves behind
  // "Show archived" automatically. Derived rather than a nightly cron – no
  // cron-lag, nothing deleted, and it can't drift from the countdown shown on
  // the brief page (both compute from closed_at + 60 days).
  const AUTO_ARCHIVE_DAYS = 60;
  const autoArchivedByAge = (r) => {
    if (r.briefs?.status !== 'closed' || !r.briefs?.closed_at) return false;
    return (Date.now() - new Date(r.briefs.closed_at).getTime()) >= AUTO_ARCHIVE_DAYS * 86400000;
  };
  const isArchivedForSupplier = (r) => Boolean(r.briefs?.archived_at) || supplierArchivedSet.has(r.brief_id) || autoArchivedByAge(r);

  const briefBucket = (r) => {
    if (r.status === 'withdrawn') return null;
    if (isArchivedForSupplier(r)) return null; // excluded from all normal tabs
    if (r.briefs?.status === 'closed') {
      return r.briefs.awarded_supplier_id === r.supplier_id ? 'awarded' : 'closed';
    }
    if (r.status === 'declined') return 'closed';
    // 2026-06: response deadline auto-close. When required_response_time
    // has passed AND Fusion hasn't already closed the brief (handled
    // above), pending / in-progress recipients drop to Closed –
    // they didn't meet the response gate. Submitted recipients
    // (status === 'responded') STAY in Submitted post-deadline so
    // the supplier can still see they made the buzzer; per the
    // user's follow-up: "submitted should stay in submitted."
    const deadlineIso = r.response_deadline || r.briefs?.required_response_time;
    if (deadlineIso && new Date(deadlineIso).getTime() < Date.now() && r.status !== 'responded') {
      return 'closed';
    }
    if (r.status === 'pending') return 'awaiting';
    if (r.status === 'responded') return 'submitted';
    return 'in_progress';
  };

  // Award-visibility helpers. A recipient is "awarded to this supplier" only
  // when the brief is closed AND the awarded_supplier_id matches. An award
  // is "unacknowledged" until the supplier opens the brief detail page –
  // at which point a row in supplier_award_acknowledgements is written with
  // acknowledged_at set. The four portal cues (welcome panel, callout strip,
  // fresh card, tab pulse) all key off this same single source of truth so
  // they resolve together on the next render.
  const isAwardedToMe = (r) => r.briefs?.status === 'closed'
    && r.briefs.awarded_supplier_id === r.supplier_id
    && !isArchivedForSupplier(r);
  const isAwardAcknowledged = (briefId) => {
    const ack = awardAcks.get(briefId);
    return !!(ack && ack.acknowledged_at);
  };
  const unacknowledgedAwards = briefs.filter(r => isAwardedToMe(r) && !isAwardAcknowledged(r.brief_id));
  const unacknowledgedAwardCount = unacknowledgedAwards.length;


  const archiveBriefForSupplier = async (briefId, briefName = '') => {
    if (!supplierUser) return;
    if (supplierArchivingIds.has(briefId) || supplierArchivedSet.has(briefId)) return;
    if (!window.confirm("Archive this brief from your view? Archived briefs are hidden from your portal. Fusion 2K will still see the brief until they archive it on their side. This can't be undone.")) return;
    const startedAt = Date.now();
    // Phase 1: row dips + fades. Phase 2 (collapse) lands 150ms later so
    // the user sees the dip register before the row shrinks out.
    setSupplierArchivingIds(prev => new Set(prev).add(briefId));
    setTimeout(() => {
      setSupplierCollapsingIds(prev => new Set(prev).add(briefId));
    }, 150);
    const { error } = await supabase.from('supplier_brief_archives')
      .upsert({ supplier_id: supplierUser.supplier_id, brief_id: briefId }, { onConflict: 'supplier_id,brief_id' });
    if (error) {
      // Rollback animation so the card settles back in place and surface
      // the failure in the toast tray.
      setSupplierArchivingIds(prev => { const n = new Set(prev); n.delete(briefId); return n; });
      setSupplierCollapsingIds(prev => { const n = new Set(prev); n.delete(briefId); return n; });
      supplierNotify({ eyebrow: "Couldn't archive", title: error.message, tone: 'archive' });
      return;
    }
    // After the choreography wraps (~500ms total) commit to the
    // truth-state set so isArchivedForSupplier filters the row out, and
    // clear the animation gates.
    setTimeout(() => {
      setSupplierArchivedSet(prev => new Set(prev).add(briefId));
      setSupplierArchivingIds(prev => { const n = new Set(prev); n.delete(briefId); return n; });
      setSupplierCollapsingIds(prev => { const n = new Set(prev); n.delete(briefId); return n; });
    }, 500);
    // Confirmation toast – no Undo per the supplier-side spec (archive is
    // one-way). Synced to land as the row finishes collapsing so the two
    // moments read as one coordinated beat.
    supplierNotify(
      {
        eyebrow: 'Archived',
        title: briefName ? `${briefName} archived` : 'Brief archived',
        body: 'Hidden from your active list.',
        tone: 'archive',
      },
      { holdMs: 4000, syncWith: startedAt },
    );
  };
  const briefTabs = [
    // "Awaiting your response", matching the status pill and the legend copy
    // below it – this tab was the one place on the portal still saying
    // "Awaiting you", so the same state had two names on one page.
    { key: 'awaiting', label: 'Awaiting your response' },
    { key: 'in_progress', label: 'In progress' },
    { key: 'submitted', label: 'Submitted' },
    { key: 'awarded', label: 'Awarded' },
    { key: 'closed', label: 'Closed' },
  ];
  const briefCounts = briefs.reduce((acc, r) => {
    const b = briefBucket(r);
    if (b == null) return acc; // withdrawn recipients aren't counted in any tab
    acc[b] = (acc[b] || 0) + 1;
    return acc;
  }, { awaiting: 0, in_progress: 0, submitted: 0, awarded: 0, closed: 0 });
  const archivedRecipients = briefs.filter(isArchivedForSupplier);

  // What bucket would this row have lived in if it weren't archived?
  // Mirrors briefBucket() but skips the isArchivedForSupplier early-out,
  // so an archived awarded brief surfaces under the Awarded tab's
  // "Show archived" toggle, an archived declined brief surfaces under
  // Closed's, etc. Returns null for the (rare) withdrawn-then-archived
  // case – those still vanish from every tab.
  const originalBucketFor = (r) => {
    if (r.status === 'withdrawn') return null;
    if (r.briefs?.status === 'closed') {
      return r.briefs.awarded_supplier_id === r.supplier_id ? 'awarded' : 'closed';
    }
    if (r.status === 'declined') return 'closed';
    // Same deadline auto-close behaviour as briefBucket (incl. the
    // "submitted stays submitted" carve-out) – keeps the archived-
    // show toggles consistent with the live tabs.
    const deadlineIso = r.response_deadline || r.briefs?.required_response_time;
    if (deadlineIso && new Date(deadlineIso).getTime() < Date.now() && r.status !== 'responded') {
      return 'closed';
    }
    if (r.status === 'pending') return 'awaiting';
    if (r.status === 'responded') return 'submitted';
    return 'in_progress';
  };
  const archivedAwardedRecipients = archivedRecipients.filter(r => originalBucketFor(r) === 'awarded');
  const archivedClosedRecipients = archivedRecipients.filter(r => originalBucketFor(r) === 'closed');

  // Per-tab sort. Briefs ship from PostgREST in insertion order otherwise,
  // which puts the most-urgent item arbitrarily anywhere in the list. The
  // tabs map onto different supplier intents so the sort key differs:
  //   - Awaiting / In Progress: soonest effective deadline first (the
  //     per-recipient deadline overrides the brief's, matching how the
  //     server-side trigger reads COALESCE(rec_dl, brief_dl))
  //   - Submitted: most recently submitted first
  //   - Awarded: newest award first (fresh awards from [[project-supplier-portal-redesign]]
  //     stay above older acknowledged ones)
  //   - Closed: most recently closed (or declined) first
  // Nulls fall to the bottom of every list.
  const getEffectiveDeadline = (r) => r.response_deadline || r.briefs?.required_response_time || null;
  const cmpDateAsc = (a, b) => {
    if (!a && !b) return 0;
    if (!a) return 1;
    if (!b) return -1;
    return new Date(a).getTime() - new Date(b).getTime();
  };
  const cmpDateDesc = (a, b) => cmpDateAsc(b, a);
  const sortByTab = (rows, tab) => {
    const copy = [...rows];
    if (tab === 'awaiting' || tab === 'in_progress') {
      copy.sort((x, y) => cmpDateAsc(getEffectiveDeadline(x), getEffectiveDeadline(y)));
    } else if (tab === 'submitted') {
      copy.sort((x, y) => cmpDateDesc(x.responded_at, y.responded_at));
    } else if (tab === 'awarded') {
      copy.sort((x, y) => cmpDateDesc(x.briefs?.closed_at, y.briefs?.closed_at));
    } else if (tab === 'closed') {
      const key = (r) => r.declined_at || r.briefs?.closed_at;
      copy.sort((x, y) => cmpDateDesc(key(x), key(y)));
    }
    return copy;
  };

  // Per-tab "Show archived" – each toggle reveals only the archived
  // rows that BELONG to that tab's bucket. Awarded only sees archived
  // awards; Closed only sees archived-not-won + archived-declined.
  // Archived rows aren't sorted client-side – supplier_brief_archives
  // doesn't expose its timestamp on this query, so any per-row date
  // would be missing for self-archives. They render in insertion order
  // beneath the sorted live list, which keeps them visually segregated.
  const filteredBriefs = (() => {
    if (activeBriefTab === 'closed' && showArchivedBriefs) {
      return [
        ...sortByTab(briefs.filter(r => briefBucket(r) === 'closed'), 'closed'),
        ...archivedClosedRecipients,
      ];
    }
    if (activeBriefTab === 'awarded' && showArchivedAwarded) {
      return [
        ...sortByTab(briefs.filter(r => briefBucket(r) === 'awarded'), 'awarded'),
        ...archivedAwardedRecipients,
      ];
    }
    return sortByTab(briefs.filter(r => briefBucket(r) === activeBriefTab), activeBriefTab);
  })();

  // Colours + labels come from the shared PortalStatusPill source (§3) so this
  // pill can't drift from the response form's. This function only maps the
  // recipient/brief state to a status KEY; the token lives in one place.
  const supplierBriefPill = (recipient) => {
    const brief = recipient.briefs;
    let key;
    if (brief?.status === 'closed') {
      key = brief.awarded_supplier_id === recipient.supplier_id ? 'awarded' : 'closed';
    } else if (recipient.status === 'declined') key = 'declined';
    else if (recipient.status === 'responded') key = 'submitted';
    else if (recipient.status === 'accepted') key = 'in_progress';
    else key = 'awaiting';
    const meta = portalStatusMeta(key);
    return { label: meta.label, tone: meta.cls };
  };

  // Deadline urgency now comes from the shared escalation scale
  // (lib/deadlineUrgency), read by every supplier surface so the list, banner,
  // detail and sticky bar can't disagree. Under 48h it switches to an hours
  // countdown and flags the brief at-risk. `nowMs` (from useNow) drives a live
  // recompute so a long-open page never shows a stale figure (§5).
  const urgencyFor = (iso, recipient) => deadlineUrgency(iso, {
    status: recipient.status,
    briefStatus: recipient.briefs?.status,
    now: nowMs,
  });
  const effectiveDeadlineOf = (r) => r.response_deadline || r.briefs?.required_response_time;

  // At-risk set (§2): started-or-awaiting briefs within 48h of closing that the
  // supplier hasn't submitted or declined. Derived, recomputed each render via
  // nowMs. Ordered by least time remaining – drives the dashboard banner, and
  // its presence suppresses the amber awaiting banner (§3).
  const atRiskBriefs = briefs
    .filter(r => !isArchivedForSupplier(r))
    .filter(r => { const b = briefBucket(r); return b === 'awaiting' || b === 'in_progress'; })
    .map(r => ({ r, u: urgencyFor(effectiveDeadlineOf(r), r) }))
    .filter(x => x.u?.atRisk)
    .sort((a, b) => a.u.msRemaining - b.u.msRemaining);
  // Which filter tiles hold an at-risk brief – drives the red tile flag (§4).
  const atRiskBucketSet = new Set(atRiskBriefs.map(({ r }) => briefBucket(r)));

  const supplierInitial = (supplierUser?.suppliers?.supplier_name || '?')
    .trim()
    .charAt(0)
    .toUpperCase();

  // British date-only – "5 Jun 2026". Cards show the date; the detail
  // page carries the time.
  const CARD_MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const fmtCardDate = (iso) => {
    if (!iso) return null;
    const d = new Date(iso);
    return `${d.getDate()} ${CARD_MONTHS[d.getMonth()]} ${d.getFullYear()}`;
  };

  // Single brief card. Aligned to the redesigned detail page: real
  // title + subtitle, British date, and triage facts (Client + Event
  // Date) read through the SAME visibility logic the detail page uses.
  // Awarded cards split into two visual states: fresh (unacknowledged –
  // green gradient + celebration header + primary CTA) and acknowledged
  // (calm panel with a compact award meta line + just Archive). The
  // split is driven by the supplier_award_acknowledgements row; opening
  // the brief detail page flips fresh → acknowledged on next render.
  const BriefCard = ({ recipient, isFirst = false }) => {
    const brief = recipient.briefs;
    const pill = supplierBriefPill(recipient);
    // Effective deadline = the supplier's per-recipient response_deadline when
    // Fusion has granted them an extension, otherwise the brief deadline.
    const effectiveDeadlineIso = recipient.response_deadline || brief?.required_response_time;
    const urgency = urgencyFor(effectiveDeadlineIso, recipient);
    const archived = isArchivedForSupplier(recipient);
    const isAwarded = isAwardedToMe(recipient);
    const isFreshAward = isAwarded && !archived && !isAwardAcknowledged(brief.id);
    // Archive surfaces on closed briefs AND on declines – a declined brief is a
    // closed conversation from the supplier's side even if Fusion hasn't yet
    // closed it globally.
    const canArchive = !archived && (brief?.status === 'closed' || recipient.status === 'declined');
    const deadlineDate = fmtCardDate(effectiveDeadlineIso);
    const awardedDate = fmtCardDate(brief?.closed_at);

    const isSupplierArchiving = supplierArchivingIds.has(brief.id);
    const isSupplierCollapsing = supplierCollapsingIds.has(brief.id);
    const animationClasses = `archive-row ${isSupplierCollapsing ? 'archive-row--archived' : ''} ${isSupplierArchiving && !isSupplierCollapsing ? 'archive-row--archiving' : ''}`;
    // Compact row (mock-aligned dashboard list). The rich detail – preview,
    // facts, next-steps, celebration – now lives on the brief page; the row
    // carries name, project, status, date + urgency, and archive. Fresh awards
    // keep a green left accent.
    const bucket = briefBucket(recipient);
    const meta = isAwarded
      ? (awardedDate ? { label: 'Won', value: awardedDate } : null)
      : bucket === 'submitted'
        ? (recipient.responded_at
            ? { label: 'Sent', value: fmtCardDate(recipient.responded_at), sub: deadlineDate ? `Deadline ${deadlineDate}` : null }
            : (deadlineDate ? { label: 'Deadline', value: deadlineDate } : null))
        : bucket === 'closed'
          ? (awardedDate ? { label: 'Closed', value: awardedDate }
              : recipient.declined_at ? { label: 'Declined', value: fmtCardDate(recipient.declined_at) } : null)
          : (deadlineDate ? { label: 'Due', value: deadlineDate } : null);
    // No FS/project-number shown to suppliers (submission-confirmation §2) –
    // internal reference. Subtitle is the project name only.
    const subtitle = brief.fusion_project_name ? String(brief.fusion_project_name).trim() : '';
    const extPending = recipient.extension_requested_at
      && (recipient.status === 'pending' || recipient.status === 'accepted')
      && recipient.briefs?.status !== 'closed';
    // Type + NDA badges, rendered inline right after the title.
    const renderBadges = () => (
      <>
        {brief.nda_required && <span className="flex-shrink-0 font-['Metropolis'] text-[9px] font-bold tracking-[0.5px] uppercase rounded px-1.5 py-0.5 bg-[#E0290A]/[0.12] text-[#E0290A]" title="NDA required to proceed">NDA</span>}
        {/* BRIEF-31 §3a – one pill, two colours: Exclusive (green) /
            Competitive (teal). Identical shape; colour is the only diff. */}
        {brief.brief_type === 'exclusive' && <span className="flex-shrink-0 font-['Metropolis'] text-[9px] font-bold tracking-[0.5px] uppercase rounded px-1.5 py-0.5" style={{ background: '#e4f2d8', color: '#2f6b1e' }} title="Exclusive – only you were invited to quote for this brief">Exclusive</span>}
        {brief.brief_type === 'competitive' && <span className="flex-shrink-0 font-['Metropolis'] text-[9px] font-bold tracking-[0.5px] uppercase rounded px-1.5 py-0.5" style={{ background: '#e2eef1', color: '#256b7a' }} title="Competitive – multiple suppliers were invited">Competitive</span>}
      </>
    );
    return (
      <div
        id={isFirst ? 't-card' : undefined}
        role="button"
        tabIndex={0}
        onClick={() => navigate(`/portal/brief/${brief.id}`)}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(`/portal/brief/${brief.id}`); } }}
        className={`group relative flex items-center gap-4 px-5 py-4 cursor-pointer transition-colors hover:bg-[#F7F9F0] focus:outline-none focus-visible:bg-[#F2F6E8] focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#009159] ${archived ? 'opacity-70' : ''} ${animationClasses}`}
      >
        {isFreshAward && <span aria-hidden="true" className="absolute left-0 top-0 bottom-0 w-[3px] bg-[#92D400]" />}
        {/* At-risk red edge (§4) – a brief within 48h of closing that the
            supplier hasn't submitted. Sits below the fresh-award edge, which
            never co-occurs (an at-risk brief is still open, not awarded). */}
        {urgency?.atRisk && <span aria-hidden="true" className="absolute left-0 top-0 bottom-0 w-[3px] bg-[#E0290A]" />}
        <div className="min-w-0 flex-1">
          {/* Title + badges inline. Badges sit right after the name (shrink-0)
              so the name gets every remaining pixel and truncates only when it
              genuinely runs out. A fixed badge COLUMN was tried (aligned badges
              down the list) but on lg+ the briefs list sits in the narrow 1.55fr
              grid column, and a 140px reserved column crushed the name to a few
              characters – showing the name wins over badge alignment. */}
          <div className="flex items-center gap-2 min-w-0">
            <h3 className="font-['Metropolis'] text-[15px] font-bold text-ink-primary truncate min-w-0">{brief.brief_name}</h3>
            <span className="flex items-center gap-1.5 flex-shrink-0">{renderBadges()}</span>
          </div>
          {/* §4 – the subtitle slot is RESERVED (min-height) so a brief with no
              project name doesn't shorten the row; rows stay a consistent
              height whether or not a subtitle is present. Truncates, never
              wraps. */}
          <p className="font-['Metropolis'] text-[12px] text-[#7C7F6F] mt-0.5 truncate min-h-[16px]">{subtitle}</p>
          {/* The deadline column is hidden on mobile, so an at-risk row surfaces
              its countdown here instead – the countdown must stay visible at
              375px (§7). Desktop shows it in the meta column on the right. */}
          {urgency?.atRisk && (
            <p className="sm:hidden font-['Metropolis'] text-[11px] font-bold text-[#E0290A] mt-1">{urgency.short} left · closing soon</p>
          )}
          {extPending && <p className="font-['Metropolis'] text-[11px] text-[#2C6B82] mt-1">More time requested – awaiting Fusion 2K.</p>}
          {recipient.status === 'declined' && recipient.decline_reason && <p className="font-['Metropolis'] text-[11px] text-[#7C7F6F] italic mt-1 truncate">Declined: &ldquo;{recipient.decline_reason}&rdquo;</p>}
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          {canArchive && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); archiveBriefForSupplier(brief.id, brief.brief_name); }}
              disabled={isSupplierArchiving}
              title="Archive brief"
              aria-label="Archive brief"
              className={`archive-button opacity-0 group-hover:opacity-100 focus:opacity-100 bg-white border border-[#2E8FA1]/50 rounded-full text-[#2C6B82] p-1.5 hover:bg-[#2E8FA1]/10 transition-[opacity,background-color] disabled:cursor-not-allowed ${isSupplierArchiving ? 'archive-button--morphed opacity-100' : ''}`}
            >
              <ArchiveIcon morphed={isSupplierArchiving} size={13} />
            </button>
          )}
          {/* Status chip wired to the shared status tokens (Partner Portal §5):
              each state carries its own fill (Awarded = the same emerald as the
              internal product) rather than a flat neutral. Archived rows stay
              neutral – that's a view-state, not a brief status. */}
          {/* §2 – the status pill sits in a FIXED-WIDTH column (sized to the
              longest label, "Awaiting your response") and is right-aligned
              within it, so the pill can hug its own text without shifting the
              date + chevron that follow. The column holds the same horizontal
              position on every row regardless of which status shows. */}
          <div className="flex justify-end flex-shrink-0 sm:w-[172px]">
            <span className={`font-['Metropolis'] text-[11px] font-semibold rounded-full px-3 py-1 border whitespace-nowrap ${archived ? 'bg-[#EEF1E6] text-[#51534A] border-[#DDE1D0]' : pill.tone}`}>{archived ? 'Archived' : pill.label}</span>
          </div>
          {/* Addendum §3: the due-date column is capped to its content
              (flex-shrink-0, was a fixed w-[132px] that reserved space the
              content rarely used) so the title takes the slack and truncates
              less. */}
          {meta && (
            <div className="hidden sm:block text-right flex-shrink-0 sm:w-[124px]">
              {/* §2: for an open deadline, lead with the RELATIVE date (toned by
                  urgency band) and show the absolute date beneath; escalate to a
                  "Closing soon" text flag when imminent/overdue. Other states
                  (Won / Sent / Closed / Declined) keep the single absolute line. */}
              {meta.label === 'Due' && urgency ? (
                <>
                  {/* Imminent (<48h) uses the compact "31h left" so the hours
                      countdown fits this fixed column; calmer bands keep the
                      relative phrase. The banner + detail carry the full form. */}
                  <span className={`block font-['Metropolis'] text-[12px] font-bold whitespace-nowrap ${urgency.tone}`}>
                    {urgency.atRisk ? `${urgency.short} left` : urgency.label}
                  </span>
                  <span className="block font-['Metropolis'] text-[11px] text-[#9AA093] whitespace-nowrap">{meta.value}</span>
                  {urgency.closingSoon && (
                    <span className="inline-block mt-1 font-['Metropolis'] text-[9px] font-bold tracking-[0.5px] uppercase text-[#E0290A] bg-[#FBE7E2] rounded-[5px] px-1.5 py-px">Closing soon</span>
                  )}
                </>
              ) : (
                <>
                  <span className="block font-['Metropolis'] text-[12px] whitespace-nowrap text-[#7C7F6F]">{meta.label} {meta.value}</span>
                  {meta.sub && <span className="block font-['Metropolis'] text-[11px] text-[#9AA093] whitespace-nowrap">{meta.sub}</span>}
                </>
              )}
            </div>
          )}
          <svg className="w-4 h-4 text-[#9AA093] flex-shrink-0 group-hover:text-[#51534A] transition-colors" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="9 6 15 12 9 18" /></svg>
        </div>
      </div>
    );
  };

  // Awaiting-tab empty state. Two flavours depending on supplier state:
  //   Red  → "blocked" empty state, points at /portal/compliance
  //   Else → quiet "nothing yet" empty state
  // Other tabs (In Progress, Awarded, Closed) keep their plain empties.
  const renderAwaitingEmpty = () => {
    if (compliance.isBlocked) {
      // Sub-case: supplier has submitted everything and is waiting on
      // Fusion approval. Different copy so they don't think they need
      // to upload again.
      const allAwaitingReview = compliance.counts.awaitingReview > 0
        && compliance.counts.outstanding === 0
        && compliance.counts.expired === 0;
      const body = allAwaitingReview
        ? "Your documents are with Fusion 2K for review. You can still respond to new briefs – we'll let you know once you're cleared to be awarded work."
        : "New briefs can still be responded to, but Fusion 2K can't award any work until your compliance is resolved.";
      // Build #18 §1c/§5: was a muddy red-tinted panel (rgba(224,41,10,.05))
      // with a THIRD red "Resolve" button – duplicating the compliance card's
      // red CTA. Neutralised to a clean white informational panel with a
      // single deep-green text link, so the dashboard keeps ONE strong red
      // (the compliance card below).
      return (
        <div className="bg-white border border-[#E6EADB] rounded-2xl shadow-lift p-10 text-center">
          <p className="font-['Metropolis'] text-sm text-ink font-semibold mb-4">{body}</p>
          {!allAwaitingReview && (
            <button
              type="button"
              onClick={() => navigate('/portal/compliance')}
              className="font-['Metropolis'] text-xs font-bold tracking-[0.04em] text-fusion-emerald hover:text-active-green underline underline-offset-2 transition-colors"
            >
              Resolve compliance →
            </button>
          )}
        </div>
      );
    }
    return (
      <div className="bg-white border border-line rounded-xl p-10 text-center">
        <p className="font-['Metropolis'] text-sm text-[#51534A] italic">
          No briefs awaiting your response. Fusion 2K will let you know when something new comes in.
        </p>
      </div>
    );
  };

  // (The old standalone "Heads up: N documents expire soon" strip was removed –
  // it fired for any amber state, so awaiting-review amber showed the nonsense
  // "0 documents expire soon". The "Your compliance" card already carries this:
  // its Renew-soon state names the expiring count with a Renew CTA, and its
  // Under-review state covers awaiting review. One place, the compliance box.)

  return (
    <div className="min-h-screen bg-canvas">
      {/* Supplier-side archive toast – confirmation only, no Undo per the
          one-way archive rule on this side. */}
      <SupplierToast />
      {/* Persistent red "action needed" top bar retired on the dashboard
          (BRIEF-25 §2/§8). Compliance urgency now lives in the welcome
          status pill and the guided compliance rail card, so the page no
          longer opens with a full-width banner. The ComplianceStripe stays
          on the other supplier pages as the slim cross-page reminder. */}

      {/* Top utility bar – left-aligned brand lockup balanced by the
          signed-in pill on the right. The lockup is one row: Fusion
          mark+wordmark PNG · thin divider · "Supply Pilot" tag, all
          vertically centred. Below sm the divider + tag drop off and
          the pill collapses to an avatar-only button (handled inside
          SupplierAccountMenu) so the header stays clean on narrow
          screens. */}
      {/* Dark header bar – matches the admin sidebar surface (#101113) so
          the portal shares the family chrome. */}
      <div className="bg-surface-sidebar border-b border-white/[0.08]">
        <div className="max-w-5xl mx-auto px-4 sm:px-10 h-[76px] sm:h-[90px] flex items-center justify-between gap-4">
          <div className="flex items-center min-w-0">
            <button type="button" onClick={() => navigate('/portal')} aria-label="Fusion 2K Supply Pilot – home" title="Home" className="flex items-center gap-2.5 flex-shrink-0 bg-transparent border-0 p-0 cursor-pointer hover:opacity-80 transition-opacity">
              <FusionLogo icon alt="Fusion" className="h-[38px] w-auto object-contain" />
              <span className="font-['Metropolis'] text-[19px] font-extrabold tracking-[0.14em] text-white leading-none">FUSION</span>
            </button>
            <div className="hidden sm:block w-px h-9 bg-white/15 flex-shrink-0 mx-3.5" />
            <span className="hidden sm:inline-flex items-center whitespace-nowrap font-['Metropolis'] text-[12px] font-bold tracking-[0.16em] uppercase text-sb-text">
              Supply Pilot <span className="text-sb-muted font-semibold">&nbsp;·&nbsp; Partner Portal</span>
            </span>
          </div>
          {supplierUser && (
            <span id="t-settings" className="inline-flex">
              <SupplierAccountMenu
                supplierName={supplierUser.suppliers?.supplier_name}
                supplierEmail={supplierUser.email || supplierUser.suppliers?.main_contact_email}
                initials={supplierInitial}
                complianceState={compliance.state}
                onOpenProfile={() => openSlideout('profile')}
                onOpenHelp={() => openSlideout('help')}
                onChangePassword={() => navigate('/change-password')}
                onReplayTour={handleReplayTour}
                onSignOut={() => { supabase.auth.signOut(); navigate('/'); }}
              />
            </span>
          )}
        </div>
      </div>

      <div className="max-w-[1140px] mx-auto px-6 sm:px-8 pt-10 pb-8">

        {/* Welcome panel – adapts when there's at least one unacknowledged
            award. Calm-state header becomes a "★ Congratulations" pill,
            the body copy names the brief (singular) or the count (plural),
            and the copy gains weight + lightens to white-ish for stronger
            presence. Reverts to the standard welcome on next render once
            the last unacknowledged award is opened. */}
        {supplierUser && (() => {
          const contactName = (supplierUser.suppliers?.main_contact_name || '').trim();
          const firstName = contactName ? contactName.split(/\s+/)[0] : '';
          // Time-of-day greeting + date, matching the admin dashboard header.
          const h = new Date().getHours();
          const timeGreeting = h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : 'Good evening';
          const dateStr = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
          // Partner Portal §3 – sage hero. Rebuilt off the warm cream
          // (#F4F1E6) that looked off-palette against the sage/white/green
          // product: a soft sage gradient with a subtle lime accent glow, so
          // the first thing a supplier sees reads as the same application.
          // Greeting stays sentence-case (a deliberate warmer external voice).
          return (
            <div className="mb-8 rounded-[20px] px-6 py-6 sm:px-8 sm:py-6 relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #EEF3E4 0%, #E2ECCF 100%)', border: '1px solid #D3DEBF', boxShadow: '0 1px 2px rgba(45,48,35,0.04), 0 10px 26px -14px rgba(45,48,35,0.14)' }}>
              {/* Subtle lime accent – a soft corner glow, not a block of colour. */}
              <span aria-hidden="true" className="pointer-events-none absolute -top-12 -right-10 w-48 h-48 rounded-full" style={{ background: 'radial-gradient(circle, rgba(146,212,0,0.20), transparent 70%)' }} />
              <div className="relative">
                <h1 className="font-['Metropolis'] text-[26px] sm:text-[32px] font-extrabold text-ink leading-tight">{timeGreeting}, {firstName || (supplierUser.suppliers?.supplier_name || 'there')}</h1>
                <div className="font-['Metropolis'] text-[13px] font-semibold text-[#6B7560] mt-1">{dateStr}</div>
                {/* §B1 – compact hero: greeting, date and ONE line. A returning
                    supplier has read the full welcome many times, so the longer
                    explanation sits behind a disclosure (native <details> – no
                    state, keyboard-operable, collapsed by default). */}
                <p className="font-['Metropolis'] text-[14px] text-[#4C4E42] leading-relaxed mt-3 max-w-[680px]">
                  Your briefs, responses and compliance – all in one place.
                </p>
                <details className="group mt-2">
                  <summary className="cursor-pointer inline-flex items-center gap-1 font-['Metropolis'] text-[13px] font-semibold text-[#2E8FA1] hover:underline list-none [&::-webkit-details-marker]:hidden focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2E8FA1]/40 rounded">
                    How the portal works
                    <span aria-hidden="true" className="transition-transform group-open:rotate-90">→</span>
                  </summary>
                  <p className="font-['Metropolis'] text-[14px] text-[#4C4E42] leading-relaxed mt-2 max-w-[680px]">
                    Supply Pilot is Fusion 2K&rsquo;s Partner Portal. Here you&rsquo;ll find the briefs you&rsquo;ve been invited to quote for, submit and track your responses, and keep your compliance documents up to date – all in one place.
                  </p>
                </details>
              </div>
            </div>
          );
        })()}

        {/* Award banner (footer/dashboard brief Part B) – the focal point of
            the page while any award is unconfirmed. White surface + green
            accent border + real shadow so it reads as an EVENT, not more pale-
            green page furniture (B2); lime primary button from the button
            system (B3); hexagon-check icon, the product's "awarded" metaphor
            (B4). Clears once the award is opened/acknowledged. Multiple awards
            collapse to one banner with a short linked list rather than
            stacking (B5). */}
        {unacknowledgedAwardCount > 0 && (() => {
          const single = unacknowledgedAwardCount === 1;
          const first = unacknowledgedAwards[0];
          const briefName = first?.briefs?.brief_name || 'this brief';
          const onView = () => navigate(`/portal/brief/${first.brief_id}`);
          return (
            <div className="mb-8 rounded-2xl bg-white shadow-[0_2px_4px_rgba(45,48,35,0.05),0_18px_40px_-16px_rgba(0,113,63,0.30)] border border-[#CFE6B5] border-l-[5px] border-l-[#009159] px-6 py-5">
              <div className="flex items-start gap-4 flex-wrap">
                {/* The medal at 40px – the award notification size. Replaces
                    the hexagon-check, which was the same superseded mark the
                    internal card carried. One mark everywhere. */}
                <AwardMedal size={40} />
                <div className="flex-1 min-w-0">
                  <div className="font-['Metropolis'] text-[11px] font-bold tracking-[0.2em] uppercase" style={{ color: 'var(--seal)' }}>Congratulations</div>
                  <h2 className="font-['Metropolis'] text-[18px] sm:text-[20px] font-extrabold text-ink leading-tight mt-0.5">
                    {single
                      ? <>You&rsquo;ve been awarded <span className="text-[#00713f]">{briefName}</span></>
                      : <>You&rsquo;ve been awarded <span className="text-[#00713f]">{unacknowledgedAwardCount} briefs</span></>}
                  </h2>
                  <p className="font-['Metropolis'] text-[13px] text-[#4C4E42] mt-1 leading-relaxed">
                    {single
                      ? 'Fusion 2K has awarded this brief to you. Open it to see your next steps.'
                      : 'Fusion 2K has awarded these briefs to you. Open each to see your next steps.'}
                  </p>
                  {/* Multiple awards: a short linked list, not stacked banners (B5). */}
                  {!single && (
                    <ul className="mt-3 flex flex-col gap-1.5">
                      {unacknowledgedAwards.map((r) => (
                        <li key={r.brief_id}>
                          <button
                            type="button"
                            onClick={() => navigate(`/portal/brief/${r.brief_id}`)}
                            className="inline-flex items-center gap-2 font-['Metropolis'] text-[13px] font-semibold text-[#009159] hover:underline text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-[#009159]/40 rounded"
                          >
                            <span aria-hidden="true" className="w-1.5 h-1.5 rounded-full bg-[#009159] flex-shrink-0" />
                            {r.briefs?.brief_name || 'View brief'}
                            {r.briefs?.awarded_amount != null && <span className="text-[#51534A] font-bold"> · £{Number(r.briefs.awarded_amount).toLocaleString('en-GB')} ex-VAT</span>}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                {single && (
                  <button type="button" onClick={onView} className="fusion-btn-primary flex-shrink-0 self-center">
                    View award →
                  </button>
                )}
              </div>
            </div>
          );
        })()}

        {/* BRIEF-25 §2 – two-column body. Briefs are the wide hero (left,
            ~1.55fr); the guided compliance + support rail sits on the right
            (~1fr). Recognition runs full-width beneath the grid. On narrow
            screens the columns stack (briefs first). */}
        <div className="grid grid-cols-1 lg:grid-cols-[1.55fr_1fr] gap-6 lg:gap-8 items-start">
          <section className="min-w-0">

        {/* Briefs section – dominant body content. Tabs + cards as
            before, plus a state-adaptive empty / soft-reminder treatment
            wired into compliance state. */}
        {/* Section header – small deep-green label + "View all" (mock). */}
        <div className="flex items-center justify-between gap-3 mb-3">
          <div className="font-['Metropolis'] text-[11px] font-bold tracking-[0.2em] uppercase text-[#009159] inline-flex items-center">
            Your briefs
            <span id="t-info" className="inline-flex align-middle">
              <InfoIcon heading="What each tab means" width={InfoIcon.WIDE} ariaLabel="What does each briefs tab mean?">
                <span className="block mb-1.5"><strong className="text-ink font-bold">Awaiting your response</strong> – briefs Fusion 2K has invited you to quote for that you haven't replied to yet.</span>
                <span className="block mb-1.5"><strong className="text-ink font-bold">In Progress</strong> – briefs you've accepted but not yet submitted a response for. Your draft is saved here.</span>
                <span className="block mb-1.5"><strong className="text-ink font-bold">Submitted</strong> – responses you've sent. Fusion 2K is considering them; nothing more to do for now.</span>
                <span className="block mb-1.5"><strong className="text-ink font-bold">Awarded</strong> – briefs you've won. Open these for next steps.</span>
                <span className="block"><strong className="text-ink font-bold">Closed</strong> – briefs no longer open to you: awarded elsewhere, withdrawn, or past their deadline.</span>
              </InfoIcon>
            </span>
          </div>
          <button type="button" onClick={() => navigate('/help/how-briefs-work')} className="font-['Metropolis'] text-[12px] font-semibold text-[#2E8FA1] hover:underline whitespace-nowrap flex-shrink-0">How briefs work →</button>
        </div>


        {briefsLoadError ? (
          // The query came back with an error – DON'T render the calm
          // "no briefs" empty state because that's confidently wrong.
          // This is the recoverable case: a refresh usually fixes it
          // once the underlying issue (often a missing-column error
          // immediately after a deploy that beat its migration) is
          // resolved on the server side.
          <div
            className="rounded-xl p-8 text-center"
            style={{ backgroundColor: 'rgba(224,41,10,0.05)', border: '1px solid rgba(224,41,10,0.4)' }}
          >
            <p className="font-['Metropolis'] text-sm text-ink font-semibold mb-2">
              We couldn't load your briefs just now.
            </p>
            <p className="font-['Metropolis'] text-xs text-fusion-mid-grey mb-4">
              Please refresh this page. If the problem persists, get in touch with the Fusion 2K team and we'll sort it.
            </p>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="inline-block bg-[#E0290A] text-white font-['Metropolis'] text-[10px] font-bold tracking-[0.15em] uppercase px-5 py-2 rounded-full hover:brightness-110 transition-[filter]"
            >
              Refresh
            </button>
          </div>
        ) : briefs.length === 0 && !compliance.isBlocked ? (
          <div className="bg-white border border-[#E6EADB] rounded-2xl shadow-lift p-12 text-center">
            <p className="font-['Metropolis'] text-[#51534A] tracking-[3px] uppercase text-sm">No briefs have been sent to you yet.</p>
          </div>
        ) : (
          <>
            {/* The award celebration lives in the "Congratulations – you've been
                awarded …" banner above the briefs grid (with View award →). The
                duplicate green "You've won …" alert that used to sit here was
                removed so the award is celebrated once, not twice. */}

            {/* Urgent deadline banner (deadline-reminder brief §3) – RED, for
                briefs within 48h the supplier hasn't submitted. Distinct from
                the amber awaiting banner, and it SUPPRESSES that banner (below)
                so the two never stack. Not dismissible – it clears when the
                response is submitted or the deadline passes. Announced politely
                (aria-live) rather than as an interrupting alert (§7). Always
                names the brief, the hours remaining, and the absolute closing
                time with its timezone. */}
            {atRiskBriefs.length > 0 && (() => {
              const single = atRiskBriefs.length === 1;
              const redBtn = 'bg-[#E0290A] text-white rounded-lg font-[\'Metropolis\'] text-[13px] font-bold px-4 py-2.5 hover:brightness-110 transition-[filter] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#E0290A]/50 focus-visible:ring-offset-2';
              return (
                <div role="status" aria-live="polite" className="bg-[#FDEBE7] border border-[#E0290A]/55 rounded-2xl px-5 py-4 mb-4 shadow-lift">
                  <div className="flex items-start gap-4 flex-wrap">
                    <span className="w-10 h-10 rounded-xl bg-[#E0290A]/15 flex items-center justify-center flex-shrink-0" aria-hidden="true">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#E0290A" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><line x1="12" y1="7" x2="12" y2="12.5" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>
                    </span>
                    <div className="flex-1 min-w-0">
                      {single ? (() => {
                        const { r, u } = atRiskBriefs[0];
                        const name = r.briefs?.brief_name || 'this brief';
                        const started = briefBucket(r) === 'in_progress';
                        const closes = formatDeadlineLondon(effectiveDeadlineOf(r));
                        return (
                          <>
                            <p className="font-['Metropolis'] text-[15px] font-extrabold text-[#8A1B08] leading-tight">
                              {started ? 'Your response ' : 'A brief '}{u.label.replace(/^Closes/, 'closes')}
                            </p>
                            <p className="font-['Metropolis'] text-[12.5px] text-[#7A2010] mt-1 leading-relaxed">
                              {started
                                ? <>You&rsquo;ve started a quote for <strong className="font-bold">{name}</strong> but haven&rsquo;t submitted it. Closes {closes}.</>
                                : <><strong className="font-bold">{name}</strong> is still awaiting your response. Closes {closes}.</>}
                            </p>
                          </>
                        );
                      })() : (
                        <>
                          <p className="font-['Metropolis'] text-[15px] font-extrabold text-[#8A1B08] leading-tight">
                            {atRiskBriefs.length} responses close within 48 hours
                          </p>
                          <ul className="mt-2 flex flex-col gap-1.5">
                            {atRiskBriefs.map(({ r, u }) => (
                              <li key={r.brief_id}>
                                <button
                                  type="button"
                                  onClick={() => navigate(`/portal/brief/${r.brief_id}`)}
                                  className="inline-flex items-baseline gap-2 text-left font-['Metropolis'] text-[13px] text-[#7A2010] hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[#E0290A]/40 rounded">
                                  <span aria-hidden="true" className="w-1.5 h-1.5 rounded-full bg-[#E0290A] flex-shrink-0 translate-y-[-2px]" />
                                  <span className="font-bold">{r.briefs?.brief_name || 'View brief'}</span>
                                  <span className="text-[#E0290A] font-bold">{u.short} left</span>
                                  <span className="text-[#9c6152]">· closes {formatDeadlineLondon(effectiveDeadlineOf(r))}</span>
                                </button>
                              </li>
                            ))}
                          </ul>
                        </>
                      )}
                    </div>
                    {single && (
                      <button
                        type="button"
                        onClick={() => navigate(`/portal/brief/${atRiskBriefs[0].r.brief_id}`)}
                        className={`${redBtn} w-full sm:w-auto flex-shrink-0 self-center`}>
                        {briefBucket(atRiskBriefs[0].r) === 'in_progress' ? 'Finish and submit →' : 'Open the brief →'}
                      </button>
                    )}
                  </div>
                </div>
              );
            })()}
            {/* The generic "N briefs awaiting your response" banner was removed
                (dashboard-alerts brief §2): it restated the Awaiting-you count
                card directly beneath it, which sits above exactly those briefs.
                Urgency is carried by the red deadline banner above; routine
                awaiting is the count card + list. */}

            {/* Changed-brief alert (merge-changed-brief-banners brief) – ONE
                amber banner replacing the old amended (blue) + clarification
                (amber) pair. The union of amended (amend_pending_since) + new-
                clarification briefs, deduped to one row per brief; the amend-vs-
                clarify distinction becomes a TAG on the row, so a brief in both
                appears once. Sorted deadline soonest-first. A single brief
                collapses to a plain Alert; 2+ get a <ul> with a Review each.
                The refresh glyph carries "changed"; amber carries "needs a
                look". Row leaves when the supplier RESUBMITS, not on open. */}
            {(() => {
              // Only changes NEWER than the supplier's last look at that brief
              // (brief_recipients.changes_seen_at, stamped when they open it).
              // These alerts used to clear only on resubmit, so clicking one
              // and coming back showed it still sitting there.
              //
              // This re-arms rather than dismisses: a later amendment or a
              // newly published clarification carries a newer timestamp than
              // changes_seen_at, so the alert comes back. Missing column (before
              // the migration runs) → seen is null → previous behaviour.
              // changesSeenAt merges the stored value with a local copy, so a
              // refused write can't leave the alert stuck (see lib/changesSeen).
              const seenAt = (r) => changesSeenAt(r);
              const isAmended = (r) => !!r.amend_pending_since
                && new Date(r.amend_pending_since).getTime() > seenAt(r);
              // newClarByBrief holds the newest published_at per brief so the
              // same "is it newer than my last look" test applies.
              const isClarified = (r) => {
                const latest = newClarByBrief.get(r.brief_id)?.latestPublishedAt;
                return !!latest && new Date(latest).getTime() > seenAt(r);
              };
              const changed = briefs
                .filter(r => !isArchivedForSupplier(r)
                  && (isAmended(r) || isClarified(r))
                  && r.briefs?.brief_name)
                .map(r => {
                  const amended = isAmended(r);
                  const clarified = isClarified(r);
                  const iso = effectiveDeadlineOf(r);
                  // The supplier has responded, so the shared scale would return
                  // null – force it with a live status so the deadline (now
                  // relevant again) still lands. Closed briefs still yield null.
                  const u = iso ? deadlineUrgency(iso, { status: 'pending', briefStatus: r.briefs?.status }) : null;
                  return {
                    id: r.brief_id,
                    name: r.briefs.brief_name,
                    ref: r.briefs.fusion_project_name ? String(r.briefs.fusion_project_name).trim() : '',
                    amended,
                    clarified,
                    tag: amended && clarified ? 'Amended + clarified' : amended ? 'Brief amended' : 'New clarification',
                    deadlineIso: iso,
                    closesLabel: u?.label || (iso ? `Closes ${formatDeadlineLondon(iso)}` : null),
                    atRisk: !!u?.atRisk,
                    quote: responsesByBrief.get(r.brief_id)?.total_price ?? null,
                  };
                })
                .sort((a, b) => {
                  const ta = a.deadlineIso ? new Date(a.deadlineIso).getTime() : Infinity;
                  const tb = b.deadlineIso ? new Date(b.deadlineIso).getTime() : Infinity;
                  return ta - tb;
                });
              if (changed.length === 0) return null;
              const gbp = (n) => `£${Number(n).toLocaleString('en-GB', { maximumFractionDigits: 0 })}`;

              // Single brief → a plain Alert, no list (brief §4).
              if (changed.length === 1) {
                const c = changed[0];
                const verb = c.amended && c.clarified ? 'amended and clarified' : c.amended ? 'amended' : 'clarified';
                const quoteLine = c.quote != null ? `Your quote of ${gbp(c.quote)} is saved.` : 'Your answers are saved.';
                return (
                  <div className="mb-4">
                    <Alert
                      tone="amber"
                      glyph={CHANGED_GLYPH}
                      title={<><strong className="font-bold">{c.name}</strong> was {verb}</>}
                      body={<>{quoteLine} Check whether your quote still stands.{c.closesLabel ? <> <span className={c.atRisk ? 'font-bold text-[#8A1B08]' : ''}>{c.closesLabel}</span>.</> : null}</>}
                      action={{ label: 'See what changed', onClick: () => navigate(`/portal/brief/${c.id}`) }}
                    />
                  </div>
                );
              }

              // 2+ → one amber panel (Alert's tokens) hosting a row per brief.
              return (
                <div className="mb-4">
                  <div
                    role="status"
                    className="relative overflow-hidden rounded-xl border font-['Metropolis']"
                    style={{ background: '#FBF3E4', borderColor: '#E89A2E40', padding: '16px 18px' }}
                  >
                    <div className="grid items-start gap-3.5" style={{ gridTemplateColumns: '40px 1fr' }}>
                      <span aria-hidden="true" className="flex items-center justify-center"
                        style={{ width: 40, height: 40, borderRadius: 10, background: '#E89A2E24' }}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#7A4E10" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{CHANGED_GLYPH}</svg>
                      </span>
                      <div className="min-w-0">
                        <p className="text-ink" style={{ fontSize: '15.5px', fontWeight: 600, lineHeight: 1.4 }}>
                          {changed.length} briefs changed after you responded
                        </p>
                        <p className="text-[#4C4E42] mt-1" style={{ fontSize: '13.5px', lineHeight: 1.5 }}>
                          Your answers are saved. Check whether your quote still stands.
                        </p>
                      </div>
                    </div>
                    <ul className="mt-3 flex flex-col gap-2">
                      {changed.map(c => (
                        <li key={c.id} className="flex items-center gap-3 flex-wrap rounded-lg bg-white/60 border border-[#E89A2E29] px-3 py-2">
                          <div className="min-w-0 flex-1">
                            <div className="text-[13px] font-semibold text-ink">
                              {c.name}{c.ref ? <span className="text-[#8A8E7F] font-normal"> · {c.ref}</span> : null}
                            </div>
                            <div className="text-[12px] mt-1 flex items-center gap-2 flex-wrap">
                              <span className="inline-flex items-center rounded-full bg-[#E89A2E1F] border border-[#E89A2E40] px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.08em] text-[#7A4E10]">{c.tag}</span>
                              {c.closesLabel && <span className={c.atRisk ? 'font-bold text-[#8A1B08]' : 'text-[#6B4A11]'}>{c.closesLabel}</span>}
                            </div>
                          </div>
                          <button type="button" onClick={() => navigate(`/portal/brief/${c.id}`)}
                            className="self-center whitespace-nowrap rounded-lg border border-[#D4D9C6] bg-white px-3.5 py-1.5 text-[12px] font-semibold text-ink hover:border-[#B9C0A9] transition-colors">
                            Review
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              );
            })()}
            {/* Filter tiles (mock-aligned) – big-number stat tiles. "All" is
                the default; "Awaiting you" gets an acid accent only when it has
                a count (neutral at 0). Awarded shows a green dot for
                unacknowledged awards. */}
            {/* §9 mobile: 2 columns so the Awaiting (action) tile can span full
                width and the other four fall into a 2×2 below it. From sm up,
                5 columns for the 5 tiles so they fill the row edge-to-edge
                (addendum §3 – was 6 cols, which left an empty tail). */}
            <div id="t-tabs" className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-3">
              {briefTabs.map(t => {
                const isActive = activeBriefTab === t.key;
                const count = briefCounts[t.key] || 0;
                const showAwardPulse = t.key === 'awarded' && unacknowledgedAwardCount > 0;
                // §2: the Awaiting tile is dominant while it has a count –
                // amber, a larger figure and an "Action needed" flag. At zero
                // it reverts to a normal quiet tile. Emphasis shows when it's
                // not the active tab (the active tab takes the green selected
                // state instead).
                const isAwaitingAction = t.key === 'awaiting' && count > 0;
                // At-risk (<48h) supersedes amber: any tile whose bucket holds
                // an at-risk brief escalates to the red "Closing soon" flag (§4).
                const isUrgentTile = atRiskBucketSet.has(t.key);
                const urgent = isUrgentTile && !isActive;
                const emphasise = isAwaitingAction && !isActive && !urgent;
                const numberColour = isActive ? 'text-[#00713f]'
                  : urgent ? 'text-[#8A1B08]'
                  : emphasise ? 'text-[#7A4E10]'
                  : count > 0 ? 'text-ink'
                  : 'text-[#9AA093]';
                return (
                  <button
                    key={t.key}
                    type="button"
                    onClick={() => setActiveBriefTab(t.key)}
                    aria-pressed={isActive}
                    className={`text-left bg-white rounded-xl border px-3 py-2.5 shadow-lift transition-[border-color,box-shadow,transform] hover:-translate-y-px flex flex-col min-h-[88px] ${
                      t.key === 'awaiting' ? 'col-span-2 sm:col-span-1' : ''
                    } ${
                      isActive
                        ? 'border-[#009159] ring-1 ring-[#009159]/25'
                        : urgent
                          ? 'border-[#E0290A]/60 bg-[#FDEBE7] hover:border-[#E0290A]'
                          : emphasise
                            ? 'border-[#E89A2E]/70 bg-[#FDF7EC] hover:border-[#E89A2E]'
                            : 'border-[#E6EADB] hover:border-[#CBD1BE]'
                    }`}>
                    <div className="flex items-center gap-1.5">
                      <span className={`font-['Metropolis'] font-extrabold leading-none ${emphasise || urgent ? 'text-[28px]' : 'text-2xl'} ${numberColour}`}>{count}</span>
                      {showAwardPulse && <span className="w-2 h-2 rounded-full bg-[#92D400] flex-shrink-0" aria-hidden="true" title="New award" />}
                    </div>
                    <div className="font-['Metropolis'] text-[11px] font-semibold text-[#51534A] mt-1 truncate">{t.label}</div>
                    {/* §1 – the "Action needed" flag renders into a slot that is
                        RESERVED on every tile (mt-auto pushes it to the bottom;
                        the fixed min-height above already fits it). So the row
                        height is identical whether or not the flag is present,
                        and switching filters never reflows the tile row. */}
                    <div className="mt-auto pt-1 h-[15px]">
                      {urgent ? (
                        <div className="inline-flex items-center gap-1 font-['Metropolis'] text-[9px] font-bold tracking-[0.4px] uppercase text-[#8A1B08]">
                          <span className="w-1.5 h-1.5 rounded-full bg-[#E0290A] flex-shrink-0" aria-hidden="true" />
                          Closing soon
                        </div>
                      ) : emphasise && (
                        <div className="inline-flex items-center gap-1 font-['Metropolis'] text-[9px] font-bold tracking-[0.4px] uppercase text-[#7A4E10]">
                          <span className="w-1.5 h-1.5 rounded-full bg-[#E89A2E] flex-shrink-0" aria-hidden="true" />
                          Action needed
                        </div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
            <p className="font-['Metropolis'] text-[12px] text-[#7C7F6F] mb-5">
              {/* Sort label must match sortByTab: awaiting/in-progress order by
                  soonest deadline; the rest by most recent. */}
              Showing {briefTabs.find(t => t.key === activeBriefTab)?.label || ''} · {
                (activeBriefTab === 'awaiting' || activeBriefTab === 'in_progress')
                  ? 'soonest deadline first'
                  : 'most recent first'
              }
            </p>

            {/* Per-tab "Show archived" toggles. Closed and Awarded each
                get their own – an archived AWARDED brief belongs in the
                supplier's wins history, not the lost-briefs pile, so the
                two surfaces are kept independent. */}
            {activeBriefTab === 'closed' && archivedClosedRecipients.length > 0 && (
              <div className="flex items-center justify-between bg-white border border-line rounded-xl px-4 py-2.5 mb-4">
                <label className="flex items-center gap-2.5 cursor-pointer">
                  <input type="checkbox" checked={showArchivedBriefs} onChange={e => setShowArchivedBriefs(e.target.checked)} className="w-4 h-4 accent-active-green" />
                  <span className="font-['Metropolis'] text-xs text-fusion-mid-grey">Show archived briefs</span>
                </label>
                <span className="font-['Metropolis'] text-xs text-[#51534A]">{archivedClosedRecipients.length} archived</span>
              </div>
            )}
            {activeBriefTab === 'awarded' && archivedAwardedRecipients.length > 0 && (
              <div className="flex items-center justify-between bg-white border border-line rounded-xl px-4 py-2.5 mb-4">
                <label className="flex items-center gap-2.5 cursor-pointer">
                  <input type="checkbox" checked={showArchivedAwarded} onChange={e => setShowArchivedAwarded(e.target.checked)} className="w-4 h-4 accent-active-green" />
                  <span className="font-['Metropolis'] text-xs text-fusion-mid-grey">Show archived briefs</span>
                </label>
                <span className="font-['Metropolis'] text-xs text-[#51534A]">{archivedAwardedRecipients.length} archived</span>
              </div>
            )}

            {filteredBriefs.length === 0 ? (
              activeBriefTab === 'awaiting' ? renderAwaitingEmpty() : (
                <div className="bg-white border border-[#E6EADB] rounded-2xl shadow-lift p-10 text-center">
                  <p className="font-['Metropolis'] text-sm text-[#51534A] italic">
                    {activeBriefTab === 'in_progress' && 'No briefs in progress. Briefs you accept and have not submitted yet will appear here.'}
                    {activeBriefTab === 'awarded' && 'No awarded briefs yet.'}
                    {activeBriefTab === 'closed' && 'No closed briefs.'}
                  </p>
                </div>
              )
            ) : (
              <div className="bg-white border border-[#E6EADB] rounded-2xl shadow-lift overflow-hidden divide-y divide-[#EDF0E5]">
                {filteredBriefs.map((r, i) => <BriefCard key={r.id} recipient={r} isFirst={i === 0} />)}
              </div>
            )}
          </>
        )}

          </section>

          {/* Guided rail (BRIEF-25 §2; rebalanced BRIEF-32 §1) – just the
              compact compliance card now (status + progress + corrected copy +
              Resolve CTA + how-it-works link). The old "Need a hand?" support
              card moved to the page footer so the rail no longer towers over
              the briefs column. Elevated on the shared shadow-lift system.
              No longer sticky (§7): the column now carries the compliance card
              AND the recent-activity list, so it fills its own height and
              scrolls with the page rather than pinning a short card and
              leaving a large empty tail. */}
          <aside className="min-w-0 flex flex-col gap-6">
            {(() => {
              // Four-state model (compliance review-loop brief §6.2), worst
              // document first: needs_attention > expiring_soon > under_review >
              // up_to_date. Derived from the SAME counts as the /compliance page
              // rollup (lib/compliance) so the two can't disagree. A green manual
              // override still reads up_to_date. Expiring-soon is AMBER, not red –
              // it's a deadline, not a failure.
              const c = compliance.counts || {};
              const total = c.total || 0;
              // Same numerator as the /compliance status card – expiring-soon
              // docs are approved (with a deadline), so they count here too.
              const rawApproved = approvedCurrentCount(c);
              const blocking = (c.outstanding || 0) + (c.expired || 0) + (c.needsClarification || 0);
              const expiringSoon = c.expiringSoon || 0;
              const awaitingReview = c.awaitingReview || 0;
              const cardState = compliance.state === 'green' ? 'up_to_date'
                : blocking > 0 ? 'needs_attention'
                : expiringSoon > 0 ? 'expiring_soon'
                : awaitingReview > 0 ? 'under_review'
                : 'up_to_date';
              const approved = cardState === 'up_to_date' ? total : rawApproved;
              const left = Math.max(0, total - approved);
              // Under review is amber at the supplier-state level (docs uploaded,
              // not yet approved) – the badge reflects that with the "In Review"
              // eye. The others map straight across.
              const badgeState = { up_to_date: 'green', under_review: 'review', expiring_soon: 'amber', needs_attention: 'red' }[cardState];
              const rc = {
                up_to_date: { title: 'Compliance up to date', label: 'Up to date', labelColor: '#009159', sub: 'All your required documents are approved and up to date – you’re ready to be awarded work.', bar: '#92D400', accent: '#009159', cta: 'View documents', solid: false },
                under_review: { title: 'With Fusion 2K', label: 'Under review', labelColor: '#2C6B82', sub: `${awaitingReview} document${awaitingReview === 1 ? ' is' : 's are'} with Fusion 2K for review – nothing more to do right now.`, bar: '#2E8FA1', accent: '#2E8FA1', cta: 'View documents', solid: false },
                expiring_soon: { title: 'Renew soon', label: 'Expiring soon', labelColor: '#B26A00', sub: `${expiringSoon} approved document${expiringSoon === 1 ? ' is' : 's are'} expiring soon – renew ${expiringSoon === 1 ? 'it' : 'them'} to stay ready for work.`, bar: '#E89A2E', accent: '#E89A2E', cta: 'Renew documents', solid: true },
                needs_attention: { title: 'Action needed', label: 'Action needed', labelColor: '#B02309', sub: `${blocking} document${blocking === 1 ? '' : 's'} need${blocking === 1 ? 's' : ''} sorting. You can still respond to briefs – Fusion 2K just can’t award you new work until ${blocking === 1 ? 'it’s' : 'they’re'} resolved.`, bar: '#E0290A', accent: '#E0290A', cta: 'Resolve compliance', solid: true },
              }[cardState];
              return (
                <div>
                  {/* Section header – deep-green label + state on the right (mock). */}
                  <div className="flex items-center justify-between gap-3 mb-3">
                    <span id="t-compliance" className="font-['Metropolis'] text-[11px] font-bold tracking-[0.2em] uppercase text-[#009159] inline-flex items-center">Your compliance<InfoIcon heading="Compliance status" ariaLabel="What does the compliance status mean?">Your compliance at a glance. Green means every required document is approved and up to date. Amber means something needs attention soon; red means a required document is outstanding – you can still respond to briefs, but Fusion 2K can't award you work until it's resolved.</InfoIcon></span>
                    {rc.label && <span className="font-['Metropolis'] text-[11px] font-bold tracking-[0.2em] uppercase" style={{ color: rc.labelColor }}>{rc.label}</span>}
                  </div>
                  <div className="bg-white border border-[#E6EADB] border-l-4 rounded-2xl shadow-lift p-6" style={{ borderLeftColor: rc.accent }}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="font-['Metropolis'] text-lg font-extrabold text-ink leading-tight">{rc.title}</div>
                      <ComplianceBadge state={badgeState} size="md" counts={compliance.counts} />
                    </div>
                    <p className="font-['Metropolis'] text-[13px] text-ink-2 mt-2 leading-relaxed">{rc.sub}</p>
                    {/* A re-signature demand doesn't fit the four card states –
                        the supplier HAS signed, so "up to date" or "expiring"
                        both mislead. Say it as its own line rather than
                        bending the state (document-versioning §7). */}
                    {(compliance.resignRequired || []).length > 0 && (
                      <p className="font-['Metropolis'] text-[12.5px] font-semibold text-[#7A4E10] bg-[#E89A2E]/[0.10] border border-[#E89A2E]/35 rounded-lg px-3 py-2 mt-3 leading-snug">
                        {compliance.resignRequired.length === 1
                          ? `${compliance.resignRequired[0].label} has been updated and needs signing again`
                          : `${compliance.resignRequired.length} documents have been updated and need signing again`}
                        {(() => {
                          const due = compliance.resignRequired.find(r => r.state === 'resign_due' && r.dueDate);
                          return due ? ` by ${fmtDate(due.dueDate)}` : '';
                        })()}.
                      </p>
                    )}
                    {total > 0 && (
                      <div className="mt-4">
                        {/* Same segmented bar as the /portal/compliance card. */}
                        <ComplianceBar counts={compliance.counts} />
                        <div className="flex items-center justify-between mt-1.5">
                          <span className="font-['Metropolis'] text-[11px] font-semibold text-[#51534A]">{approved} of {total} approved</span>
                          {cardState === 'up_to_date'
                            ? <span className="font-['Metropolis'] text-[11px] font-bold" style={{ color: rc.accent }}>all current</span>
                            : left > 0 && <span className="font-['Metropolis'] text-[11px] font-bold" style={{ color: rc.accent }}>{left} left</span>}
                        </div>
                      </div>
                    )}
                    <div className="mt-5 flex flex-col gap-2.5">
                      <button
                        type="button"
                        onClick={() => navigate('/portal/compliance')}
                        className={`w-full font-['Metropolis'] text-[13px] font-bold rounded-lg px-4 py-3 transition-[filter,transform] hover:-translate-y-px ${
                          cardState === 'needs_attention'
                            ? 'bg-[#E0290A] text-white hover:brightness-105 shadow-[0_2px_8px_rgba(224,41,10,0.32)]'
                            : rc.solid
                              ? 'bg-[#009159] text-white hover:brightness-105 shadow-[0_2px_8px_rgba(0,145,89,0.28)]'
                              : 'bg-white text-[#009159] border-[1.5px] border-[#B7D9BF] hover:border-[#009159]'
                        }`}
                      >
                        {rc.cta} →
                      </button>
                      <button
                        type="button"
                        onClick={() => navigate('/help/compliance')}
                        className="font-['Metropolis'] text-[12px] font-semibold text-[#2E8FA1] hover:underline underline-offset-2 text-center"
                      >
                        How compliance works →
                      </button>
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* §7 – recent-activity list fills the right column below the
                compliance card (was a full-width strip below the grid, which
                left this column ending well short of the briefs). Real signals
                only – awards, earned badges, compliance – most-recent first,
                capped at three, hidden when nothing qualifies. */}
            {(() => {
              const nowMs = Date.now();
              const ago = (iso) => {
                if (!iso) return 'recently';
                const days = Math.floor((nowMs - new Date(iso).getTime()) / 86400000);
                if (days <= 0) return 'today';
                if (days === 1) return '1 day ago';
                if (days < 7) return `${days} days ago`;
                if (days < 30) return `${Math.floor(days / 7)}w ago`;
                return `${Math.floor(days / 30)}mo ago`;
              };
              const items = [];
              briefs.filter(r => isAwardedToMe(r) && !isArchivedForSupplier(r)).forEach(r => {
                items.push({ kind: 'award', type: 'Awarded', title: r.briefs?.brief_name || 'Brief', detail: 'You won this brief', time: r.briefs?.closed_at, onClick: () => navigate(`/portal/brief/${r.brief_id}`) });
              });
              // Closed-brief outcomes that earn a permanent What's new entry
              // (Brief 3 §5): not_selected, no_award, draft_unsent. 'won' is the
              // award entry above; no_response + declined get nothing.
              briefs.filter(r => r.briefs?.status === 'closed' && !isArchivedForSupplier(r) && !isAwardedToMe(r)).forEach(r => {
                const outcome = deriveClosedOutcome({ brief: r.briefs, recipient: r, response: responsesByBrief.get(r.brief_id), supplierId: supplierUser?.supplier_id });
                const copy = outcome && CLOSED_OUTCOME_COPY[outcome];
                if (!copy || !copy.whatsNew) return;
                items.push({ kind: 'outcome', type: 'Brief outcome', title: r.briefs?.brief_name || 'Brief', detail: copy.heading, time: r.briefs?.closed_at, onClick: () => navigate(`/portal/brief/${r.brief_id}`) });
              });
              // §5/§7 – a clarification was published after this supplier
              // submitted; their quote may now be priced against stale scope.
              newClarByBrief.forEach((clar, briefId) => {
                const r = briefs.find(x => x.brief_id === briefId);
                // Same seen-test as the banner above: once they've opened the
                // brief this stops being news. Without it, What's New kept
                // announcing clarifications the supplier had already read.
                const seen = changesSeenAt(r);
                if (clar.latestPublishedAt && new Date(clar.latestPublishedAt).getTime() <= seen) return;
                const count = clar.count;
                items.push({ kind: 'outcome', type: 'Clarification', title: r?.briefs?.brief_name || 'Brief',
                  detail: `${count} new clarification${count === 1 ? '' : 's'} since you submitted – check your quote still stands`,
                  time: clar.latestPublishedAt, onClick: () => navigate(`/portal/brief/${briefId}`) });
              });
              supplierBadges.forEach(b => {
                const current = !b.expires_at || new Date(b.expires_at).getTime() >= nowMs;
                if (!current) return;
                const cat = badgeCategoryMap.get(b.category_id);
                const isEco = cat?.slug === 'eco';
                items.push({ kind: 'recognition', eco: isEco, slug: cat?.slug, type: 'Recognition', title: `${cat?.name || 'Badge'} earned`, detail: 'A new badge on your profile', time: b.awarded_at,
                  // Eco recognition carries its growth stage so the activity
                  // card can render the actual growing-oak image, not a generic
                  // icon (the oak IS the eco badge).
                  ecoFrame: isEco ? ecoStageToFrame(isEcoStage(b.tier) ? b.tier : 'acorn') : null,
                  onClick: () => navigate('/help/recognition') });
              });
              if (compliance.state === 'green') {
                items.push({ kind: 'compliance', type: 'Compliance', title: 'Fully compliant', detail: 'All your required documents are approved', time: null, onClick: () => navigate('/portal/compliance') });
              }
              if (items.length === 0) return null;
              items.sort((a, b) => new Date(b.time || 0).getTime() - new Date(a.time || 0).getTime());
              const top = items.slice(0, 3);
              // What's New items are status/brand signals, so every icon is a
              // hexagon with a status-token fill (dashboard §6 / two-tier system
              // – footer §6). The glyph varies by kind; the growing-oak
              // illustration is NOT used here (modals only), so eco recognition
              // gets a leaf glyph, not the OakStatusBadge.
              const cfg = {
                award:       { glyph: <path d="M8 21h8M12 17v4M7 4h10v4a5 5 0 0 1-10 0V4zM17 5h2a2 2 0 0 1-2 3.5M7 5H5a2 2 0 0 0 2 3.5" /> },
                recognition:      { glyph: <><path d="M5 21c0-8 5-14 14-14 0 8-5 14-14 14z" /><path d="M5 21c3.5-4 7.5-7 12-9" /></> },
                recognitionOther: { glyph: <><path d="M12 3l7 2.5v5.5c0 4.2-3 7.3-7 8.8-4-1.5-7-4.6-7-8.8V5.5L12 3z" /><path d="M9 11.5l2 2 4-4" /></> },
                compliance:  { glyph: <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10zM9 12l2 2 4-4" /> },
                // Brief outcome – a neutral document/flag glyph (not a status).
                outcome:     { glyph: <><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /></> },
              };
              return (
                <div>
                  <div className="flex items-center justify-between gap-3 mb-3">
                    <span className="font-['Metropolis'] text-[11px] font-bold tracking-[0.2em] uppercase text-[#009159]">What&rsquo;s new</span>
                  </div>
                  <div className="bg-white border border-[#E6EADB] rounded-2xl shadow-lift divide-y divide-[#EEF1E6] overflow-hidden">
                    {top.map((it, i) => {
                      const c = it.kind === 'recognition' ? (it.eco ? cfg.recognition : cfg.recognitionOther) : cfg[it.kind];
                      const shade = it.kind === 'recognition' ? (CHIP_SHADE[it.slug] || CHIP_SHADE.eco) : (CHIP_SHADE[it.kind] || CHIP_FALLBACK);
                      return (
                        <button
                          key={i}
                          type="button"
                          onClick={it.onClick}
                          className="w-full text-left flex items-start gap-3 px-4 py-3.5 hover:bg-[#F7F9F0] transition-colors focus:outline-none focus-visible:bg-[#F2F6E8] focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#009159]"
                        >
                          {/* What's New icons are status hexagons (§6), EXCEPT
                              eco recognition – the growing-oak/acorn IS the eco
                              badge, so it renders the OakStatusBadge illustration
                              at its earned stage to match the acorn shown on the
                              recognition cards + library. */}
                          <span className="flex-shrink-0 mt-0.5" aria-hidden="true">
                            {it.eco
                              ? <OakStatusBadge frame={it.ecoFrame ?? 0} size={36} animate={false} />
                              : it.kind === 'compliance'
                                // Use the real green compliance badge (metallic
                                // hexagon + tick), matching the compliance card,
                                // not a generic grey shield glyph.
                                ? <ComplianceBadge state="green" size="sm" ariaLabel="" />
                                : it.kind === 'award'
                                  // The award MEDAL, at the notification size
                                  // from the rollout brief. It replaced the
                                  // streamers row mark, which was left behind
                                  // when the medal went in everywhere else – so
                                  // the supplier's own "you won this" row was
                                  // the one place still showing the old art.
                                  //
                                  // No tinted tile behind it: the medal is the
                                  // signal, and a chip around it adds a second
                                  // one. Decorative, because the row already
                                  // says "Awarded" in words.
                                  ? <AwardMedal size={40} decorative />
                                  : <StatusHexagon glyph={c.glyph} fg={shade.fg} size={36} />}
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="block font-['Metropolis'] text-[10px] font-bold tracking-[0.14em] uppercase text-[#7C7F6F]">{it.type} · {ago(it.time)}</span>
                            <span className="block font-['Metropolis'] text-[13px] font-bold text-ink mt-0.5 truncate">{it.title}</span>
                            <span className="block font-['Metropolis'] text-[11px] text-[#7C7F6F] mt-0.5 leading-relaxed">{it.detail}</span>
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

          </aside>
        </div>

        {/* Recognition section – moved to the foot of the dashboard
            with a de-emphasised mid-grey heading. Still shown across
            all compliance states so the badges aren't gated behind
            compliance. */}
        {(badgeCategoryMap.size > 0 || compliance.state === 'green') && (() => {
          const TIER_RANK = { bronze: 1, silver: 2, gold: 3 };
          const now = Date.now();
          const badgeByCategory = new Map();
          for (const b of supplierBadges) {
            const isCurrent = !b.expires_at || new Date(b.expires_at).getTime() >= now;
            if (!isCurrent) continue;
            const prev = badgeByCategory.get(b.category_id);
            if (!prev || (TIER_RANK[b.tier] || 0) > (TIER_RANK[prev.tier] || 0)) {
              badgeByCategory.set(b.category_id, b);
            }
          }
          const tierNameFor = (cat, tier) =>
            tier === 'gold' ? cat.gold_name : tier === 'silver' ? cat.silver_name : cat.bronze_name;
          const sortedCategories = Array.from(badgeCategoryMap.values()).sort((a, b) => {
            const aEarned = badgeByCategory.has(a.id) ? 0 : 1;
            const bEarned = badgeByCategory.has(b.id) ? 0 : 1;
            if (aEarned !== bEarned) return aEarned - bEarned;
            return (a.name || '').localeCompare(b.name || '');
          });
          // Compliance now lives in its own section above recognition, so
          // it no longer counts toward the recognition tally.
          const earnedCount = badgeByCategory.size;
          const availableCount = badgeCategoryMap.size - badgeByCategory.size;
          return (
            <div id="t-recognition" className="mt-12 pt-8 border-t border-line">
              <div className="flex items-center justify-between gap-3 mb-4">
                <span className="font-['Metropolis'] text-[11px] font-bold tracking-[0.2em] uppercase text-[#009159] inline-flex items-center">
                  Grow your recognition
                  <InfoIcon heading="Recognition badges" ariaLabel="What are recognition badges?">Badges show Fusion 2K your credentials. Earn each by meeting its requirements – earned badges appear on every brief you're considered for.</InfoIcon>
                </span>
                <span className="font-['Metropolis'] text-[11px] font-bold tracking-[0.14em] uppercase text-[#7C7F6F]">
                  {earnedCount} of {earnedCount + availableCount} earned
                </span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {sortedCategories.map(cat => {
                  const held = badgeByCategory.get(cat.id);
                  const earned = Boolean(held);
                  const tierName = earned ? tierNameFor(cat, held.tier) : tierNameFor(cat, 'bronze');
                  const nextTier = earned
                    ? (held.tier === 'bronze' ? 'silver' : held.tier === 'silver' ? 'gold' : null)
                    : null;
                  const nextTierName = nextTier ? tierNameFor(cat, nextTier) : null;
                  const renews = earned && held.expires_at
                    ? fmtDate(held.expires_at)
                    : null;
                  // Eco card uses the growing-oak model: stage from the stored
                  // badge, % derived from the oak frame. Gold "earned" styling
                  // is reserved for Oak Status; growing stages read in-progress.
                  const isEcoCard = cat.slug === 'eco';
                  const chip = CHIP_SHADE[cat.slug] || CHIP_FALLBACK;
                  const ecoStage = isEcoCard ? (held && isEcoStage(held.tier) ? held.tier : 'acorn') : null;
                  const ecoPct = isEcoCard ? Math.round((ecoStageToFrame(ecoStage) / 5) * 100) : 0;
                  const ecoIsOak = isEcoCard && ecoStage === 'oak_status';
                  const cardEarned = isEcoCard ? ecoIsOak : earned;
                  // Action-needed hold: Fusion issued a new/refreshed eco
                  // document, so the oak holds its current picture but needs
                  // the new doc completing. Surfaced as an amber prompt; the
                  // oak artwork stays put (no visible step-back).
                  const ecoActionNeeded = isEcoCard && !!held?.action_needed;
                  // Progress (dashboard FIX §2) – tiers earned for tiered
                  // categories (0..3), oak growth stage for Eco (0..5). Shown
                  // even at zero so recognition reads as an achievable system,
                  // not a wall of empty locks. Deep-green bar on a white card.
                  const progTotal = isEcoCard ? 5 : 3;
                  const progValue = isEcoCard ? ecoStageToFrame(ecoStage) : (earned ? (TIER_RANK[held.tier] || 0) : 0);
                  const progPct = Math.round((progValue / progTotal) * 100);
                  return (
                    <div
                      key={cat.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => navigate('/help/recognition')}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate('/help/recognition'); } }}
                      className="relative overflow-hidden bg-white rounded-2xl border border-[#E6EADB] shadow-lift hover:shadow-lift-hover hover:-translate-y-0.5 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-[#009159] transition-[transform,box-shadow,border-color] duration-150">
                      <span aria-hidden="true" className={`absolute left-0 top-0 bottom-0 w-[3px] ${cardEarned ? 'bg-[#009159]' : progValue > 0 ? 'bg-[#92D400]' : 'bg-[#CBD1BE]'}`} />
                      <div className="flex items-start gap-4 pl-5 pr-4 pt-5">
                        {/* Consistent flat icon in a tinted square (mock) –
                            tree for Eco (oak metaphor), shield for the rest. */}
                        {isEcoCard ? (
                          // Eco shows the growing-oak illustration itself (acorn
                          // at stage 0 → full oak at Oak Status), not a flat chip
                          // icon – the badge IS the acorn-scale.
                          <div className="w-11 h-11 flex items-center justify-center flex-shrink-0 mt-0.5" aria-hidden="true">
                            <OakStatusBadge frame={ecoStageToFrame(ecoStage)} size={38} animate={false} />
                          </div>
                        ) : (
                          <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5" style={{ background: chip.bg }} aria-hidden="true">
                            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke={chip.fg} strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3l7 2.5v5.5c0 4.2-3 7.3-7 8.8-4-1.5-7-4.6-7-8.8V5.5L12 3z"/><path d="M9 11.5l2 2 4-4"/></svg>
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="font-['Metropolis'] text-[10px] font-bold tracking-[0.2em] uppercase text-[#009159] inline-flex items-center gap-2 flex-wrap">
                            {cat.name}
                            {isEcoCard && (
                              <span className="inline-flex align-middle" onClick={e => e.stopPropagation()} onKeyDown={e => e.stopPropagation()}>
                                <InfoIcon heading="Eco · Oak Status" ariaLabel="How does the eco award work?">Your eco award grows as Fusion 2K approves your eco documents – from an acorn to a full oak. Complete them all to reach Oak Status, the top eco recognition.</InfoIcon>
                              </span>
                            )}
                            {/* Only the Eco award is live today; other categories are flagged coming soon. */}
                            {!isEcoCard && <span className="font-['Metropolis'] text-[9px] font-bold tracking-[0.14em] uppercase rounded-full px-2 py-0.5 bg-[#E4EAD4] text-[#57603F] border border-[#C4CDAC]">Coming soon</span>}
                          </div>
                          <div className="font-['Metropolis'] text-base font-bold text-ink mt-1">
                            {isEcoCard
                              ? (ecoIsOak ? 'Oak Status' : ecoStage === 'acorn' ? 'Eco – get started' : 'Growing your oak')
                              : (earned ? `You're ${tierName}` : `Earn ${tierName}`)}
                          </div>
                          <div className="font-['Metropolis'] text-[11px] text-fusion-mid-grey mt-1 leading-relaxed">
                            {isEcoCard
                              ? (ecoActionNeeded
                                  ? <span className="text-[#2C6B82] font-semibold">Action needed – Fusion 2K has issued a new eco document. Complete it in your compliance area to grow your oak back.</span>
                                  : ecoIsOak
                                      ? 'Fully grown – top eco recognition.'
                                      : ecoStage === 'acorn'
                                        // "Complete", not "Approve" — approving is
                                        // Fusion's act, and this card is the
                                        // supplier's. Telling them to approve their
                                        // own documents describes a thing they
                                        // cannot do.
                                        ? 'Complete your eco documents to grow your oak.'
                                        : `${ecoPct}% grown – complete the rest of your eco documents to reach Oak Status.`)
                              : (earned
                                  ? (nextTierName ? `Next: ${nextTierName}` : 'Top tier – congratulations.')
                                  : 'Get started – visible to Fusion 2K on every brief.')}
                            {!isEcoCard && renews && <> · Renews {renews}</>}
                          </div>
                        </div>
                      </div>
                      {/* Progress bar – deep-green, always shown (even 0 of X). */}
                      <div className="px-5 pt-4">
                        <div className="flex items-center justify-between mb-1.5">
                          <span className="font-['Metropolis'] text-[10px] font-bold tracking-[0.12em] uppercase text-[#51534A]">{progValue} of {progTotal} {isEcoCard ? 'stages' : 'tiers'}</span>
                          {cardEarned && <span className="font-['Metropolis'] text-[10px] font-bold tracking-[0.12em] uppercase text-[#009159]">Earned</span>}
                        </div>
                        <div className="h-1.5 rounded-full bg-[#E6E9DA] overflow-hidden">
                          <div className="h-full rounded-full transition-all duration-500" style={{ width: `${progPct}%`, background: 'linear-gradient(90deg, #92D400, #009159)' }} />
                        </div>
                      </div>
                      <div className="px-5 pb-4 pt-3.5">
                        <span className="font-['Metropolis'] text-[10px] font-bold tracking-[0.2em] uppercase" style={{ color: isEcoCard ? '#2E8FA1' : '#9AA093' }}>
                          {isEcoCard ? 'View Requirements →' : 'Coming soon'}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })()}

      </div>

      <SupplierSlideout
        open={slideoutOpen}
        onClose={() => setSlideoutOpen(false)}
        initialTab={slideoutTab}
        supplier={supplierUser?.suppliers}
        supplierUser={supplierUser} />

      {/* Badge Earned modal – unchanged from the previous design. */}
      {earnModalBadge && (() => {
        const cat = badgeCategoryMap.get(earnModalBadge.category_id);
        const tier = earnModalBadge.tier;
        // Eco only ever fires this modal at Oak Status (first_seen_at is left
        // null only then) – so the eco celebration is the full-oak moment.
        const isEcoModal = cat?.slug === 'eco';
        const tierName = isEcoModal ? 'Oak Status' : (cat ? (tier === 'gold' ? cat.gold_name : tier === 'silver' ? cat.silver_name : cat.bronze_name) : tier);
        const awarded = fmtDate(earnModalBadge.awarded_at);
        const renews = earnModalBadge.expires_at ? fmtDate(earnModalBadge.expires_at) : "Doesn't expire";
        return (
          <Modal open onClose={dismissEarnModal} maxWidth={520} stripe={false} light ariaLabel="Achievement unlocked">
            <Modal.Body>
              {/* BRIEF-30 – brand-only confetti (greens); no Otto on the
                  supplier celebration. */}
              <ConfettiBurst />
              <div className="text-center">
                {/* Light-green celebration hero (sanctioned treatment) */}
                <div className="rounded-2xl px-6 pt-6 pb-7" style={{ background: 'linear-gradient(135deg, #F1F8E7, #DCEFC6)', border: '1px solid #CDE3B4' }}>
                  <div className="award-badge-pop inline-flex justify-center mb-4">
                    <span className="award-badge-glow inline-flex">
                      <Badge tier={tier} size="xl" categoryLabel={cat?.name} categorySlug={cat?.slug} tierName={tierName} showLabel />
                    </span>
                  </div>
                  <div className="font-['Metropolis'] text-[11px] font-extrabold tracking-[0.25em] uppercase text-[#00713f] mb-2">{isEcoModal ? 'Eco milestone' : 'Badge earned'}</div>
                  <h2 className="font-['Metropolis'] text-[22px] font-extrabold text-ink leading-tight mb-2">{isEcoModal ? 'Congratulations – you’ve reached Oak Status' : `You've reached ${tierName}`}</h2>
                  <p className="font-['Metropolis'] text-[13px] text-[#3a4a2a] leading-relaxed max-w-[420px] mx-auto">
                    {isEcoModal
                      ? 'Your eco documents are all approved – your oak is fully grown!'
                      : <>Your engagement with the source document unlocked the {tier.charAt(0).toUpperCase() + tier.slice(1)} tier. Visible to Fusion 2K on every brief.</>}
                  </p>
                </div>
                <div className="bg-[#F7F9F0] border border-[#EAF0DD] rounded-xl px-5 py-3 text-left mt-5">
                  <div className="flex items-center justify-between py-1.5">
                    <span className="font-['Metropolis'] text-[10px] font-bold tracking-[0.15em] uppercase text-[#51534A]">Awarded</span>
                    <span className="font-['Metropolis'] text-xs font-semibold text-ink">{awarded}</span>
                  </div>
                  {/* §4 – the Category row restated the badge label/kicker
                      already shown above, so it's gone. Metadata now carries
                      only what ISN'T already said: the awarded date, plus the
                      renewal date for time-limited (non-eco) badges. */}
                  {!isEcoModal && (
                    <div className="flex items-center justify-between py-1.5 border-t border-[#EAF0DD]">
                      <span className="font-['Metropolis'] text-[10px] font-bold tracking-[0.15em] uppercase text-[#51534A]">Renews</span>
                      <span className="font-['Metropolis'] text-xs font-semibold text-ink">{renews}</span>
                    </div>
                  )}
                </div>
                {/* §4 – standard portal button order: secondary LEFT, primary
                    RIGHT (matches review / extension / decline modals). */}
                <div className="flex items-center justify-center gap-3 mt-6">
                  <button
                    type="button"
                    onClick={dismissEarnModal}
                    className="bg-white border border-[#D4D9C6] rounded-lg font-['Metropolis'] text-[13px] font-semibold text-[#51534A] px-5 py-2.5 hover:border-[#B9C0A9] transition-colors">
                    Great, thanks
                  </button>
                  <button
                    type="button"
                    onClick={() => { dismissEarnModal(); document.getElementById('t-recognition')?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }}
                    className="bg-[#92D400] rounded-lg font-['Metropolis'] text-[13px] font-bold text-[#131313] px-5 py-2.5 shadow-[0_2px_8px_rgba(146,212,0,0.30)] hover:brightness-105 hover:-translate-y-px transition-[filter,transform]">
                    See my recognition →
                  </button>
                </div>
              </div>
            </Modal.Body>
          </Modal>
        );
      })()}

      {/* Award celebration modal (award-modal brief §2/§3) – the illustrated
          celebration language (trophy, soft green gradient) that's the
          deliberate second visual system for good news, distinct from the
          functional hexagons. Sage scrim / focus trap / Escape / focus-return
          all come from the shared Modal. Dismissing leaves the banner intact
          (banner is the persistent, actionable route). Single vs multiple. */}
      {awardModalAwards && awardModalAwards.length > 0 && (() => {
        const single = awardModalAwards.length === 1;
        const first = awardModalAwards[0];
        const fb = first.briefs;
        const viewSingle = () => { dismissAwardModal(); navigate(`/portal/brief/${first.brief_id}`); };
        const viewAll = () => { dismissAwardModal(); setActiveBriefTab('awarded'); document.getElementById('t-tabs')?.scrollIntoView({ behavior: 'smooth', block: 'start' }); };
        return (
          <Modal open onClose={dismissAwardModal} maxWidth={520} stripe={false} light ariaLabel={single ? 'You have been awarded a brief' : `You have been awarded ${awardModalAwards.length} briefs`}>
            <Modal.Body>
              <ConfettiBurst />
              <div className="text-center">
                <div className="rounded-2xl px-6 pt-6 pb-7" style={{ background: 'linear-gradient(135deg, #F1F8E7, #DCEFC6)', border: '1px solid #CDE3B4' }}>
                  {/* Illustrated trophy – decorative, aria-hidden; success is
                      also conveyed in the text below (§5). */}
                  <div className="award-badge-pop inline-flex justify-center mb-4">
                    <svg width="88" height="88" viewBox="0 0 96 96" fill="none" aria-hidden="true">
                      <ellipse cx="48" cy="86" rx="26" ry="5" fill="#009159" opacity="0.14" />
                      <path d="M30 20h36v10a18 18 0 0 1-36 0V20z" fill="#92D400" />
                      <path d="M30 20h36v6a18 18 0 0 1-36 0v-6z" fill="#009159" opacity="0.18" />
                      <path d="M66 22h7a7 7 0 0 1-7 10" stroke="#009159" strokeWidth="4" strokeLinecap="round" fill="none" />
                      <path d="M30 22h-7a7 7 0 0 0 7 10" stroke="#009159" strokeWidth="4" strokeLinecap="round" fill="none" />
                      <rect x="44" y="48" width="8" height="12" fill="#009159" />
                      <path d="M36 66h24l-2 6H38l-2-6z" fill="#00713f" />
                      <path d="M44 30l2.6 4.7 5.2.9-3.7 3.7.8 5.2L44 42.7l-4.7 1.5.8-5.2-3.7-3.7 5.2-.9L44 30z" fill="#ffffff" opacity="0.85" />
                      <circle cx="74" cy="16" r="2.2" fill="#92D400" />
                      <circle cx="20" cy="28" r="1.8" fill="#009159" opacity="0.5" />
                      <circle cx="72" cy="40" r="1.6" fill="#009159" opacity="0.4" />
                    </svg>
                  </div>
                  <div className="font-['Metropolis'] text-[11px] font-extrabold tracking-[0.25em] uppercase text-[#00713f] mb-2">Congratulations</div>
                  <h2 className="font-['Metropolis'] text-[22px] font-extrabold text-ink leading-tight mb-2">
                    {single
                      ? <>You&rsquo;ve won <span className="text-[#00713f]">{fb?.brief_name || 'this brief'}</span></>
                      : <>You&rsquo;ve won <span className="text-[#00713f]">{awardModalAwards.length} briefs</span></>}
                  </h2>
                  <p className="font-['Metropolis'] text-[13px] text-[#3a4a2a] leading-relaxed max-w-[420px] mx-auto">
                    {single
                      ? 'Fusion 2K has awarded you this work. Open the brief for your next steps.'
                      : 'Fusion 2K has awarded you these briefs. Open each for its next steps.'}
                  </p>
                </div>

                {single ? (
                  /* Metadata carries only what isn't already said (§4):
                     awarded amount + awarded date. */
                  (fb?.awarded_amount != null || fb?.closed_at) && (
                    <div className="bg-[#F7F9F0] border border-[#EAF0DD] rounded-xl px-5 py-3 text-left mt-5">
                      {fb?.awarded_amount != null && (
                        <div className="flex items-center justify-between py-1.5">
                          <span className="font-['Metropolis'] text-[10px] font-bold tracking-[0.15em] uppercase text-[#51534A]">Awarded value</span>
                          <span className="font-['Metropolis'] text-sm font-extrabold text-[#00713f]">£{Number(fb.awarded_amount).toLocaleString('en-GB')} <span className="text-[11px] font-bold">ex-VAT</span></span>
                        </div>
                      )}
                      {fb?.closed_at && (
                        <div className={`flex items-center justify-between py-1.5 ${fb?.awarded_amount != null ? 'border-t border-[#EAF0DD]' : ''}`}>
                          <span className="font-['Metropolis'] text-[10px] font-bold tracking-[0.15em] uppercase text-[#51534A]">Awarded</span>
                          <span className="font-['Metropolis'] text-xs font-semibold text-ink">{fmtDate(fb.closed_at)}</span>
                        </div>
                      )}
                    </div>
                  )
                ) : (
                  /* Multiple: a list, each row linking to its brief (§2). */
                  <ul className="text-left mt-5 flex flex-col gap-1.5">
                    {awardModalAwards.map((r) => (
                      <li key={r.brief_id}>
                        <button
                          type="button"
                          onClick={() => { dismissAwardModal(); navigate(`/portal/brief/${r.brief_id}`); }}
                          className="w-full flex items-center gap-2 bg-[#F7F9F0] border border-[#EAF0DD] rounded-lg px-3.5 py-2.5 hover:border-[#CDE3B4] hover:bg-[#F1F6E7] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#009159]/40"
                        >
                          <span aria-hidden="true" className="w-1.5 h-1.5 rounded-full bg-[#009159] flex-shrink-0" />
                          <span className="font-['Metropolis'] text-[13px] font-bold text-ink truncate min-w-0 flex-1">{r.briefs?.brief_name || 'View brief'}</span>
                          {r.briefs?.awarded_amount != null && <span className="font-['Metropolis'] text-[12px] font-bold text-[#00713f] flex-shrink-0">£{Number(r.briefs.awarded_amount).toLocaleString('en-GB')} ex-VAT</span>}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}

                {/* Secondary-left / primary-right (§4). */}
                <div className="flex items-center justify-center gap-3 mt-6">
                  <button
                    type="button"
                    onClick={dismissAwardModal}
                    className="bg-white border border-[#D4D9C6] rounded-lg font-['Metropolis'] text-[13px] font-semibold text-[#51534A] px-5 py-2.5 hover:border-[#B9C0A9] transition-colors">
                    Great, thanks
                  </button>
                  <button
                    type="button"
                    onClick={single ? viewSingle : viewAll}
                    className="bg-[#92D400] rounded-lg font-['Metropolis'] text-[13px] font-bold text-[#131313] px-5 py-2.5 shadow-[0_2px_8px_rgba(146,212,0,0.30)] hover:brightness-105 hover:-translate-y-px transition-[filter,transform]">
                    {single ? 'View award →' : 'View awards →'}
                  </button>
                </div>
              </div>
            </Modal.Body>
          </Modal>
        );
      })()}

      {/* Full-bleed band – no container wrapper; the footer aligns its own
          content to the page container internally. */}
      <SupplierFooter onBrowseHelp={() => openSlideout('help')} />

      {/* Onboarding spotlight tour – auto-runs on first login, replayable
          from the launcher. Mounted last so its anchor targets (#t-tabs
          etc.) already exist in the DOM when it measures them. */}
      {tour.open && (
        <SupplierTour
          steps={SUPPLIER_TOUR_STEPS}
          currentPath={location.pathname}
          ready={!loading}
          onNavigate={navigate}
          onClose={handleTourClose}
          tipsEnabled={tipsEnabled}
          onTipsToggle={handleTipsToggle}
          infoTipsOn={infoTipsEnabled()}
          onInfoTipsToggle={setInfoTipsEnabled}
        />
      )}
    </div>
  );
}
